import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
    BarChart, LineChart, PieChart, Activity, Table, Hash, Type, Plus, Database, PaintBucket,
    LayoutTemplate, Grid, Map, MoreHorizontal, ScatterChart, ArrowLeft, ChevronDown, ChevronRight,
    Sigma, Trash2, Radar, Binary, Disc, Workflow, Filter, Cloud, Globe, Image,
    TrendingUp, AreaChart, Layers, ArrowUpDown, Minus, Gauge, AlertCircle, Share2, Clock,
    Circle, Box, GitBranch, MapPin, Navigation, Edit3, Edit2, FileText, Calendar, Layout,
    Maximize, MessageSquare, List, ArrowRight, ArrowUp, Sparkles, Palette, Eye
} from 'lucide-react';

// Import Centralized Defaults (for theming/branding)
import { GLOBAL_DEFAULTS, CHART_DEFAULTS, TABLE_DEFAULTS, WIDGET_DEFAULTS, COLOR_PALETTES } from '@/lib/chartDefaults';
import { apiFetch } from '@/api';

/* ... imports kept same ... */



/* 
  WidgetToolbox (Unified Right Sidebar):
 
  Tabs:
  1. Build: 
     - Gallery (No Selection)
     - Data Configuration (Selection): Dataset -> Fields -> Drop Zones -> Query Options (Accordionized)
  2. Format:
     - Accordion-based styling (Inclusive Expansion).
*/

export const WidgetToolbox = ({
    onWidgetAdd,
    onWidgetUpdate,
    selectedWidget,
    selectionTrigger,
    onDataModelChange,
    customMeasures = [],
    initialDataModel = null,
    isLocked = false,
    onEditMeasure = null,
    onDeleteMeasure = null,
    onPreviewTable = null,
}) => {
    const [activeTab, setActiveTab] = useState('visuals');
    const [hoveredChart, setHoveredChart] = useState(null);
    // Persist selected data model across tab switches
    const [selectedDataModelId, setSelectedDataModelId] = useState<string>('');
    const [selectedDataModel, setSelectedDataModel] = useState<any>(null);

    // Sync from parent when dashboard loads a saved data model
    useEffect(() => {
        if (initialDataModel && initialDataModel.id) {
            setSelectedDataModelId(String(initialDataModel.id));
            setSelectedDataModel(initialDataModel);
        }
    }, [initialDataModel?.id]);

    const handleDataModelChange = (id: string, model: any) => {
        setSelectedDataModelId(id);
        setSelectedDataModel(model);
        if (onDataModelChange) onDataModelChange(model);
    };

    // Build a dataset-compatible shape from the selected data model
    // so DataConfigView can show dimensions (all table columns) and measures
    // Map custom measures into a field-compatible shape
    const customMeasureFields = (customMeasures || []).map((m: any) => ({
        name: m.name,
        expression: m.formula || m.expression,
        type: 'number',
        isCustom: true,
    }));

    const adaptedDataset = selectedDataModel ? {
        name: selectedDataModel.name,
        dimensions: (selectedDataModel.tables || []).flatMap((t: any) =>
            (t.columns || []).map((col: any) => ({
                name: `${t.name}.${col.name}`,
                type: col.data_type === 'number' ? 'number'
                    : col.data_type === 'date' ? 'date'
                        : col.data_type === 'boolean' ? 'boolean'
                            : 'text',
                table: t.name,
            }))
        ),
        // Merge model-level measures with user-created custom measures
        measures: [
            ...(selectedDataModel.measures || []).map((m: any) => ({
                name: m.name,
                expression: m.expression,
                type: 'number',
            })),
            ...customMeasureFields,
        ],
    } : (customMeasureFields.length > 0 ? {
        // No data model selected but custom measures exist — show them anyway
        name: 'Custom Measures',
        dimensions: [],
        measures: customMeasureFields,
    } : null);

    // Auto-switch based on selection
    useEffect(() => {
        if (selectedWidget) {
            const isFormatOnly = selectedWidget.type === 'text' || selectedWidget.type === 'image';

            // FIX: If switching between widgets, maintain "Format" tab if active.
            // Only defaults to "Fields" if coming from generic tabs (Visuals/Data).
            if (activeTab === 'visuals' || activeTab === 'data') {
                setActiveTab(isFormatOnly ? 'format' : 'fields');
            } else if (isFormatOnly && activeTab === 'fields') {
                // If we were on 'fields' (e.g. from another chart) but text/image don't have fields, format
                setActiveTab('format');
            }
        } else {
            setActiveTab('visuals');
        }
    }, [selectedWidget]); // Removed selectionTrigger to prevent forced resets

    // Dynamic Tabs based on context
    const tabs = selectedWidget
        ? (selectedWidget.type === 'text' || selectedWidget.type === 'image'
            ? [{ id: 'format', label: 'Format', icon: <PaintBucket className="w-4 h-4" /> }]
            : [
                { id: 'fields', label: 'Fields', icon: <Database className="w-4 h-4" /> },
                { id: 'format', label: 'Format', icon: <PaintBucket className="w-4 h-4" /> }
            ])
        : [
            { id: 'visuals', label: 'Visuals', icon: <LayoutTemplate className="w-4 h-4" /> },
            { id: 'data', label: 'Data', icon: <Database className="w-4 h-4" /> }
        ];

    // Quick Add Handler - Dynamic & Robust
    const handleQuickAdd = (field, type) => {
        if (!selectedWidget || !onWidgetUpdate) return;

        const currentConfig = selectedWidget.config || {};
        let newConfig = { ...currentConfig };

        // 1. Get Zones for this specific widget type
        const widgetZones = CHART_ZONE_CONFIG[selectedWidget.type] || CHART_ZONE_CONFIG.bar;

        // 2. Define Priority Targets based on semantic meaning
        const MEASURE_TARGETS = ['yAxis', 'series', 'value', 'size', 'columns', 'target', 'values', 'lineMeasures', 'forecast', 'confidence', 'comparison', 'min', 'max'];
        const DIMENSION_TARGETS = ['xAxis', 'columns', 'rows', 'source', 'target', 'legend', 'color', 'group', 'column'];

        // 3. Find Best Zone
        let targetZone = null;

        if (type === 'measure') {
            // Find first available zone that acts as a Measure target
            const match = widgetZones.find(z => MEASURE_TARGETS.includes(z.key));
            if (match) targetZone = match.key;
        } else {
            // Find first available zone that acts as a Dimension target
            const match = widgetZones.find(z => DIMENSION_TARGETS.includes(z.key));
            if (match) targetZone = match.key;
        }

        // 4. Fallback Safety
        if (!targetZone) {
            if (type === 'measure') targetZone = 'yAxis';
            else targetZone = 'xAxis';
        }

        // console.log(`[QuickAdd] Adding ${field.name} (${type}) to ${selectedWidget.type} -> ${targetZone}`);

        // 5. Add Field to Zone
        const currentList = [...(newConfig[targetZone] || [])];

        // Check duplicates (by name)
        if (!currentList.some(f => f.name === field.name)) {
            // Create Entry
            let newEntry = {
                ...field,
                // Default Aggregation logic
                agg: (type === 'measure' && !['columns', 'rows'].includes(targetZone)) ? 'sum' : '-'
            };

            // Special defaults for specific zones
            if (['target', 'comparison', 'min', 'max'].includes(targetZone)) {
                newEntry.agg = 'sum';
            }

            currentList.push(newEntry);
            newConfig[targetZone] = currentList;

            // Auto-Infer xField if adding to xAxis (for older chart config compat)
            if (targetZone === 'xAxis' && !newConfig.xField) {
                newConfig.xField = field.name;
            }
        }

        onWidgetUpdate(selectedWidget.id, { config: newConfig });
        setActiveTab('fields');
    };

    return (
        <div className="flex flex-col h-full bg-[#fafafa] dark:bg-slate-800 border-l border-[#d6d6d6] dark:border-slate-700 shadow-lg transition-all relative" style={{ fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
            {/* TABS (Unified Header) */}
            <div className="flex items-center border-b border-[#d6d6d6] dark:border-slate-700 bg-[#f3f3f3] dark:bg-slate-800">
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`
                            flex-1 py-2.5 text-[11px] font-semibold uppercase tracking-wider transition-colors flex items-center justify-center gap-1.5
                            ${activeTab === tab.id ? 'text-[#118DF0] border-b-2 border-[#118DF0] bg-white dark:bg-slate-700 dark:text-blue-400 dark:border-blue-400' : 'text-[#605e5c] dark:text-slate-400 hover:text-[#252423] dark:hover:text-slate-200 hover:bg-[#e1dfdd] dark:hover:bg-slate-700'}
                        `}
                    >
                        {tab.icon}
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* CONTENT AREA */}
            <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
                {activeTab === 'visuals' && (
                    <GalleryView
                        onWidgetAdd={onWidgetAdd}
                        onWidgetUpdate={onWidgetUpdate}
                        selectedWidget={selectedWidget}
                        hasSelection={!!selectedWidget}
                        onHover={setHoveredChart} // Pass hover handler
                    />
                )}

                {activeTab === 'data' && (
                    <DataModelSelectorView
                        onDataModelChange={onDataModelChange}
                        selectedId={selectedDataModelId}
                        selectedModel={selectedDataModel}
                        onSelectionChange={handleDataModelChange}
                        isLocked={isLocked}
                        customMeasures={customMeasures}
                        onEditMeasure={onEditMeasure}
                        onPreviewTable={onPreviewTable}
                    />
                )}

                {activeTab === 'fields' && selectedWidget && (
                    <DataConfigView
                        selectedWidget={selectedWidget}
                        dataset={adaptedDataset}
                        activeDataModel={selectedDataModel}
                        onWidgetUpdate={onWidgetUpdate}
                        onQuickAdd={handleQuickAdd}
                        customMeasures={customMeasures} // Pass customMeasures down
                        onEditMeasure={onEditMeasure}
                        onDeleteMeasure={onDeleteMeasure}
                    />
                )}

                {activeTab === 'format' && selectedWidget && (
                    <FormatConfigView
                        widget={selectedWidget}
                        dataset={adaptedDataset}
                        onWidgetUpdate={onWidgetUpdate}
                    />
                )}
            </div>

            {/* GLOBAL TOOLTIP OVERLAY (Avoids Clipping) */}
            <GlobalTooltip type={hoveredChart} />
        </div>
    );
};

// --- SUB-COMPONENTS ---

/* DATA MODEL SELECTOR VIEW
   - Fetches all data models from /datamodels
   - Lets user pick one from a dropdown
   - Shows its Tables (expandable with typed columns) and Measures inline
*/
const DataModelSelectorView = ({ onDataModelChange, selectedId: initId = '', selectedModel: initModel = null, onSelectionChange, isLocked = false, customMeasures = [], onEditMeasure = null, onPreviewTable = null }) => {
    const [models, setModels] = useState<any[]>([]);
    const [selectedId, setSelectedId] = useState<string>(initId);
    const [selectedModel, setSelectedModel] = useState<any>(initModel);
    const [loading, setLoading] = useState(true);
    const [isChanging, setIsChanging] = useState(false);
    const [expandedTables, setExpandedTables] = useState<Record<string, boolean>>({});
    const [sectionsOpen, setSectionsOpen] = useState({ tables: true, measures: true });

    // Sync from parent when props change (e.g. tab switch restores state)
    useEffect(() => { setSelectedId(initId); }, [initId]);
    useEffect(() => { setSelectedModel(initModel); }, [initModel]);

    // Fetch all data models on mount
    useEffect(() => {
        setLoading(true);
        apiFetch('/datamodels')
            .then((data: any) => {
                const list = Array.isArray(data) ? data : (data?.items || data?.data || []);
                setModels(list);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, []);

    const handleSelect = (id: string) => {
        setSelectedId(id);
        setIsChanging(false);
        setExpandedTables({});
        setSelectedModel(null);
        // Fetch full model details (list endpoint doesn't include tables/measures)
        apiFetch(`/datamodels/${id}`)
            .then((data: any) => {
                setSelectedModel(data);
                if (onSelectionChange) onSelectionChange(id, data);
                if (onDataModelChange) onDataModelChange(data);
            })
            .catch(() => {
                const model = models.find(m => String(m.id) === id);
                setSelectedModel(model || null);
                if (onSelectionChange) onSelectionChange(id, model || null);
                if (onDataModelChange) onDataModelChange(model || null);
            });
    };

    const toggleTable = (tName: string) =>
        setExpandedTables(prev => ({ ...prev, [tName]: !prev[tName] }));

    const tables: any[] = selectedModel?.tables || [];// Merge model measures + user custom measures (avoid duplicates by name)
    const modelMeasures: any[] = selectedModel?.measures || [];
    const customMeasureItems: any[] = (customMeasures || []).map((m: any) => ({ id: m.id, name: m.name, expression: m.formula || m.expression, isCustom: true, }));
    const existingNames = new Set(modelMeasures.map((m: any) => m.name));
    const measures: any[] = [...modelMeasures, ...customMeasureItems.filter((m: any) => !existingNames.has(m.name))];

    return (
        <div className="flex flex-col gap-3">
            {/* ── SELECTOR CARD ── */}
            <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                    <Database className="w-4 h-4 text-indigo-600" />
                    <span className="text-xs font-bold text-indigo-900 uppercase tracking-wider">Active Data Model</span>
                </div>

                {(!selectedModel || isChanging) ? (
                    <div className="relative group">
                        <select
                            className={`w-full text-sm p-2 pr-8 border border-slate-300 rounded-md appearance-none bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 hover:border-indigo-400 transition-all ${isLocked ? 'cursor-not-allowed opacity-70' : ''}`}
                            value={selectedId}
                            onChange={e => handleSelect(e.target.value)}
                            disabled={loading || isLocked}
                        >
                            <option value="" disabled>{loading ? 'Loading...' : 'Select a Data Model...'}</option>
                            {models.map(m => (
                                <option key={m.id} value={m.id}>{m.name}</option>
                            ))}
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                        {isChanging && selectedModel && !isLocked && (
                            <button
                                onClick={() => setIsChanging(false)}
                                className="absolute right-9 top-1/2 -translate-y-1/2 text-[10px] font-bold text-slate-400 hover:text-red-500 uppercase bg-white px-1"
                            >Cancel</button>
                        )}
                        {/* Locked Message */}
                        {isLocked && (
                            <div className="flex items-start gap-2 mt-2 text-[10px] text-orange-600 bg-orange-50 p-2 rounded border border-orange-100">
                                <AlertCircle className="w-3 h-3 shrink-0 mt-0.5" />
                                <span>Dataset is locked because widgets are currently using data fields. Remove fields to change dataset.</span>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="flex items-center gap-2 bg-white border border-indigo-200 rounded-md p-2 shadow-sm min-h-[38px]">
                        <Database className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                        <span className="text-sm font-medium text-slate-900 truncate flex-1 px-0.5" title={selectedModel.name}>
                            {selectedModel.name}
                        </span>
                        <button
                            onClick={() => !isLocked && setIsChanging(true)}
                            disabled={isLocked}
                            className={`text-[10px] font-bold uppercase tracking-tight px-2 py-1 rounded border transition-all ${isLocked ? 'text-slate-400 border-slate-100 bg-slate-50 cursor-not-allowed' : 'text-indigo-600 border-indigo-100 bg-indigo-50 hover:bg-indigo-600 hover:text-white hover:border-indigo-600'}`}
                            title={isLocked ? "Cannot change: Data model is in use by widgets" : "Change Data Model"}
                        >Change</button>
                    </div>
                )}

                {/* Summary badges */}
                {selectedModel && !isChanging && (
                    <div className="flex gap-2 mt-2">
                        <span className="flex items-center gap-1 text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-semibold">
                            <Table className="w-2.5 h-2.5" />{tables.length} Tables
                        </span>
                        <span className="flex items-center gap-1 text-[10px] bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-semibold">
                            <Sigma className="w-2.5 h-2.5" />{measures.length} Measures
                        </span>
                    </div>
                )}
            </div>

            {/* ── MODEL CONTENT ── */}
            {selectedModel && !isChanging && (
                <div className="border border-slate-200 rounded-lg overflow-hidden">

                    {/* TABLES */}
                    <div>
                        <button
                            onClick={() => setSectionsOpen(p => ({ ...p, tables: !p.tables }))}
                            className="w-full flex items-center gap-1.5 px-3 py-2 bg-slate-50 hover:bg-slate-100 border-b border-slate-100 transition-colors"
                        >
                            {sectionsOpen.tables
                                ? <ChevronDown className="w-3 h-3 text-slate-400" />
                                : <ChevronRight className="w-3 h-3 text-slate-400" />
                            }
                            <Table className="w-3 h-3 text-blue-500" />
                            <span className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">Tables</span>
                            <span className="ml-auto text-[9px] text-slate-400 bg-slate-200 rounded-full px-1.5 py-0.5">{tables.length}</span>
                        </button>

                        {sectionsOpen.tables && (
                            <div className="bg-white divide-y divide-slate-50">
                                {tables.length === 0 && (
                                    <div className="px-4 py-2.5 text-[10px] text-slate-400 italic">No tables defined in this model</div>
                                )}
                                {tables.map((t: any) => (
                                    <div key={t.id || t.name}>
                                        <button
                                            onClick={() => toggleTable(t.name)}
                                            className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-blue-50 transition-colors group"
                                        >
                                            {expandedTables[t.name]
                                                ? <ChevronDown className="w-3 h-3 text-slate-300 shrink-0" />
                                                : <ChevronRight className="w-3 h-3 text-slate-300 shrink-0" />
                                            }
                                            <Table className="w-3 h-3 text-blue-400 shrink-0" />
                                            <span className="text-xs text-slate-700 font-medium truncate flex-1 text-left">{t.name}</span>

                                            {/* Preview Eye Icon (visible on hover) */}
                                            {onPreviewTable && (
                                                <div
                                                    onClick={(e) => { e.stopPropagation(); onPreviewTable(t.name); }}
                                                    className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-all mr-1"
                                                    title="Preview Table Data"
                                                >
                                                    <Eye size={14} />
                                                </div>
                                            )}

                                            {t.type && (
                                                <span className={`text-[8px] px-1.5 py-0.5 rounded font-bold uppercase ${t.type === 'VIRTUAL' ? 'bg-purple-100 text-purple-600' : 'bg-blue-100 text-blue-600'}`}>
                                                    {t.type}
                                                </span>
                                            )}
                                        </button>

                                        {expandedTables[t.name] && (
                                            <div className="bg-slate-50/50 pl-8 pr-2 pb-1 border-t border-slate-100">
                                                {(t.columns || []).length === 0 && (
                                                    <div className="text-[10px] text-slate-400 italic px-2 py-1.5">No columns</div>
                                                )}
                                                {(t.columns || []).map((col: any, i: number) => (
                                                    <div
                                                        key={col.name || i}
                                                        draggable
                                                        className="flex items-center gap-1.5 px-2 py-1 text-[11px] text-slate-600 hover:bg-blue-50 rounded cursor-grab active:cursor-grabbing my-0.5"
                                                    >
                                                        <span className={`text-[8px] font-bold uppercase px-1 py-0.5 rounded min-w-[24px] text-center ${/int|float|decimal|numeric|double|real/.test((col.data_type || '').toLowerCase())
                                                            ? 'bg-orange-100 text-orange-600'
                                                            : /date|time|timestamp/.test((col.data_type || '').toLowerCase())
                                                                ? 'bg-green-100 text-green-600'
                                                                : 'bg-slate-100 text-slate-500'
                                                            }`}>
                                                            {col.data_type ? col.data_type.slice(0, 3).toUpperCase() : 'STR'}
                                                        </span>
                                                        <span className="truncate flex-1">{col.name}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* MEASURES */}
                    <div>
                        <button
                            onClick={() => setSectionsOpen(p => ({ ...p, measures: !p.measures }))}
                            className="w-full flex items-center gap-1.5 px-3 py-2 bg-slate-50 hover:bg-slate-100 border-t border-slate-200 transition-colors"
                        >
                            {sectionsOpen.measures
                                ? <ChevronDown className="w-3 h-3 text-slate-400" />
                                : <ChevronRight className="w-3 h-3 text-slate-400" />
                            }
                            <Sigma className="w-3 h-3 text-orange-500" />
                            <span className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">Measures</span>
                            <span className="ml-auto text-[9px] text-slate-400 bg-slate-200 rounded-full px-1.5 py-0.5">{measures.length}</span>
                        </button>

                        {sectionsOpen.measures && (
                            <div className="bg-white divide-y divide-slate-50">
                                {measures.length === 0 && (
                                    <div className="px-4 py-2.5 text-[10px] text-slate-400 italic">No measures defined in this model</div>
                                )}
                                {measures.map((m: any) => {
                                    const rawCustom = customMeasures.find((cm: any) => cm.name === m.name);
                                    const isCustom = !!rawCustom;
                                    return (
                                        <div
                                            key={m.id || m.name}
                                            draggable
                                            className="flex items-center gap-2 px-3 py-1.5 hover:bg-orange-50 cursor-grab active:cursor-grabbing transition-colors group"
                                        >
                                            <Sigma className={`w-3 h-3 shrink-0 ${isCustom ? 'text-blue-500' : 'text-orange-400'}`} />
                                            <div className="flex-1 min-w-0">                <div className="text-xs text-slate-700 font-medium truncate">{m.name}</div>
                                                {m.expression && (
                                                    <div className="text-[9px] text-slate-400 truncate font-mono mt-0.5">
                                                        {m.expression}
                                                    </div>
                                                )}
                                            </div>
                                            {isCustom && onEditMeasure && (
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); onEditMeasure(rawCustom); }}
                                                    className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-all"
                                                    title="Edit Measure"
                                                >
                                                    <Edit2 className="w-3 h-3" />
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Empty state */}
            {!selectedModel && !loading && (
                <div className="flex flex-col items-center justify-center py-8 text-center text-slate-400">
                    <Database className="w-8 h-8 mb-2 opacity-30" />
                    <p className="text-sm font-medium">No Data Model Selected</p>
                    <p className="text-[10px] mt-1">Choose a model above to see its tables and measures.</p>
                </div>
            )}
        </div>
    );
};


/* 1. GALLERY VIEW */
const GalleryView = ({ onWidgetAdd, onWidgetUpdate, selectedWidget, hasSelection, onHover }) => {
    const onDragStart = (e, type, config = {}, label = "") => {
        e.dataTransfer.setData("text/plain", type);
        e.dataTransfer.setData("application/json", JSON.stringify({ type, source: 'toolbox', config, label }));
        e.dataTransfer.effectAllowed = "copy";
    };

    const handleAction = (type, presetConfig = {}) => {
        if (hasSelection && selectedWidget) {
            // REPLACE Behavior
            if (onWidgetUpdate) {
                // Merge preset config if exists
                const updatePayload: any = { type: type };
                if (Object.keys(presetConfig).length > 0) {
                    const pConfig: any = presetConfig;
                    updatePayload.config = { ...selectedWidget.config, ...presetConfig };

                    // Deep merge style if present
                    if (pConfig.style) {
                        updatePayload.config = {
                            ...(selectedWidget.config || {}),
                            style: {
                                ...(selectedWidget.config?.style || {}),
                                ...pConfig.style,
                                // Deep merge specific keys if needed
                                [type]: {
                                    ...(selectedWidget.config?.style?.[type] || {}),
                                    ...(pConfig.style?.[type] || {})
                                }
                            }
                        };
                    }
                }
                onWidgetUpdate(selectedWidget.id, updatePayload);
            }
        } else {
            // ADD Behavior
            if (onWidgetAdd) onWidgetAdd(type, presetConfig);
        }
    };

    // Shared Props
    const itemProps = (type, label, icon, presetConfig = {}) => ({
        type, label, icon,
        onDragStart: (e) => onDragStart(e, type, presetConfig, label),
        onClick: () => handleAction(type, presetConfig), // Pass preset config
        onHover: onHover
    });

    return (
        <div className="space-y-6">
            {hasSelection && (
                <div className="mb-2 p-2 bg-blue-50 border border-blue-100 rounded text-[10px] text-blue-700 font-bold text-center">
                    Select a chart type to replace the current "{selectedWidget.type}" widget.
                </div>
            )}

            {/* A. TRENDS (BLUE) */}
            <Section label="Analyze Trends" icon={<TrendingUp className="w-3 h-3 text-blue-500" />}>
                <div className="grid grid-cols-4 gap-2">
                    <ToolboxItem {...itemProps('line', 'Line', <LineChart className="w-5 h-5 text-blue-500" />)} />
                    <ToolboxItem {...itemProps('line', 'Area', <AreaChart className="w-5 h-5 text-blue-500" />, { style: { line: { area: true } } })} />
                    <ToolboxItem {...itemProps('stepLine', 'Step Line', <TrendingUp className="w-5 h-5 text-blue-500" />)} />
                    <ToolboxItem {...itemProps('combo', 'Combo', <Activity className="w-5 h-5 text-blue-500" />)} />
                    <ToolboxItem {...itemProps('forecast', 'Forecast', <TrendingUp className="w-5 h-5 text-blue-500 opacity-70" />)} />
                    <ToolboxItem {...itemProps('boxplot', 'Box Plot', <BarChart className="w-5 h-5 text-blue-500 rotate-90" />)} />
                </div>
            </Section>

            {/* B. COMPARE & RANK (INDIGO) */}
            <Section label="Compare & Rank" icon={<ArrowUpDown className="w-3 h-3 text-indigo-500" />}>
                <div className="grid grid-cols-4 gap-2">
                    <ToolboxItem {...itemProps('bar', 'Bar', <BarChart className="w-5 h-5 text-indigo-500" />)} />
                    <ToolboxItem {...itemProps('bar', 'Grouped', <BarChart className="w-5 h-5 text-indigo-500" />, { style: { bar: { stacked: false } } })} />
                    <ToolboxItem {...itemProps('bar', 'Stacked', <BarChart className="w-5 h-5 text-indigo-500" />, { style: { bar: { stacked: true, stackType: 'normal' } } })} />
                    <ToolboxItem {...itemProps('bar', '100% Stacked', <BarChart className="w-5 h-5 text-indigo-500" />, { style: { bar: { stacked: true, stackType: '100%' } } })} />
                    <ToolboxItem {...itemProps('waterfall', 'Waterfall', <BarChart className="w-5 h-5 text-indigo-500 rotate-90 opacity-70" />)} />
                    <ToolboxItem {...itemProps('bullet', 'Bullet', <Minus className="w-5 h-5 text-indigo-500" />)} />
                </div>
            </Section>

            {/* C. COMPOSITION (VIOLET) */}
            <Section label="Composition" icon={<PieChart className="w-3 h-3 text-violet-500" />}>
                <div className="grid grid-cols-4 gap-2">
                    <ToolboxItem {...itemProps('pie', 'Pie', <PieChart className="w-5 h-5 text-violet-500" />)} />
                    <ToolboxItem {...itemProps('donut', 'Donut', <Disc className="w-5 h-5 text-violet-500" />)} />
                    <ToolboxItem {...itemProps('bar', '100% Stacked', <BarChart className="w-5 h-5 text-violet-500 opacity-70" />, { style: { bar: { stacked: true, stackType: '100%' } } })} />
                    <ToolboxItem {...itemProps('treemap', 'Treemap', <Binary className="w-5 h-5 text-violet-500" />)} />
                </div>
            </Section>

            {/* D. PERFORMANCE (EMERALD) */}
            <Section label="Performance" icon={<Gauge className="w-3 h-3 text-emerald-500" />}>
                <div className="grid grid-cols-4 gap-2">
                    <ToolboxItem {...itemProps('kpi', 'KPI', <Activity className="w-5 h-5 text-emerald-500" />)} />
                    <ToolboxItem {...itemProps('gauge', 'Gauge', <Gauge className="w-5 h-5 text-emerald-500" />)} />
                    <ToolboxItem {...itemProps('sparkline', 'Sparkline', <TrendingUp className="w-5 h-5 text-emerald-500" />)} />
                    <ToolboxItem {...itemProps('ragKpi', 'RAG Status', <AlertCircle className="w-5 h-5 text-emerald-500" />)} />
                </div>
            </Section>

            {/* E. FLOW (CYAN) */}
            <Section label="Flow" icon={<Workflow className="w-3 h-3 text-cyan-500" />}>
                <div className="grid grid-cols-4 gap-2">
                    <ToolboxItem {...itemProps('sankey', 'Sankey', <Workflow className="w-5 h-5 text-cyan-500" />)} />
                    <ToolboxItem {...itemProps('funnel', 'Funnel', <Filter className="w-5 h-5 text-cyan-500" />)} />
                </div>
            </Section>

            {/* F. RELATIONSHIPS (ROSE) */}
            <Section label="Relationships" icon={<ScatterChart className="w-3 h-3 text-rose-500" />}>
                <div className="grid grid-cols-4 gap-2">
                    <ToolboxItem {...itemProps('scatter', 'Scatter', <ScatterChart className="w-5 h-5 text-rose-500" />)} />
                    <ToolboxItem {...itemProps('bubble', 'Bubble', <Circle className="w-5 h-5 text-rose-500" />)} />
                    <ToolboxItem {...itemProps('heatmap', 'Heatmap', <Grid className="w-5 h-5 text-rose-500" />)} />
                </div>
            </Section>

            {/* G. GEOGRAPHIC (AMBER) */}
            <Section label="Geographic" icon={<Globe className="w-3 h-3 text-amber-500" />}>
                <div className="grid grid-cols-4 gap-2">
                    <ToolboxItem {...itemProps('map', 'Geo Map', <Globe className="w-5 h-5 text-amber-500" />)} />
                    <ToolboxItem {...itemProps('choropleth', 'Choropleth', <Map className="w-5 h-5 text-amber-500" />)} />
                </div>
            </Section>

            {/* H. DETAILS (SLATE) */}
            <Section label="Details & Narrative" icon={<FileText className="w-3 h-3 text-slate-500" />}>
                <div className="grid grid-cols-4 gap-2">
                    <ToolboxItem {...itemProps('table', 'Table', <Table className="w-5 h-5 text-slate-500" />)} />
                    <ToolboxItem {...itemProps('pivot', 'Pivot', <Table className="w-5 h-5 text-slate-500 opacity-70" />)} />
                    <ToolboxItem {...itemProps('text', 'Text/Rich', <Type className="w-5 h-5 text-slate-500" />, { style: { showTitle: false }, text: '<h3>Rich Text Widget</h3><p>Enter your content here...</p>' })} />
                    <ToolboxItem
                        {...itemProps('pageHeader', 'Page Header', <Layout className="w-5 h-5 text-indigo-500" />, {
                            style: { showTitle: false, backgroundColor: 'transparent' },
                            title: 'Dashboard Title',
                            description: 'Subtitle goes here',
                            showLogo: true
                        })}
                    />
                    <ToolboxItem
                        {...itemProps('image', 'Image', <Image className="w-5 h-5 text-teal-500" />, {
                            style: { showTitle: false },
                            image: { imageUrl: '', objectFit: 'contain', align: 'center', opacity: 100, borderRadius: 0, showBorder: false, borderColor: '#d6d6d6' }
                        })}
                    />
                </div>
            </Section>

            {/* I. CONTROLS (GRAY) */}
            <Section label="Controls" icon={<Filter className="w-3 h-3 text-slate-500" />}>
                <div className="grid grid-cols-4 gap-2">
                    <ToolboxItem {...itemProps('filter', 'Filter', <Filter className="w-5 h-5 text-slate-500" />)} />
                </div>
            </Section>
        </div>
    );
};

// --- CHART ZONE CONFIGURATION (17 Types) ---
const CHART_ZONE_CONFIG = {
    // 1. Forecast
    forecast: [
        { key: 'xAxis', label: 'Time Field (Date/Month)' },
        { key: 'series', label: 'Actual Measure' },
        { key: 'forecast', label: 'Forecast Measure' },
        { key: 'confidence', label: 'Confidence Band (Optional)' }
    ],
    // 2. Sparkline
    sparkline: [
        { key: 'xAxis', label: 'Trend Field (Dimension)' },
        { key: 'yAxis', label: 'Value Metric' }
    ],
    // 3. KPI
    kpi: [
        { key: 'yAxis', label: 'Main Metric' },
        { key: 'target', label: 'Target (Optional)' },
        { key: 'comparison', label: 'Comparison Metric (Optional)' }
    ],
    // 4. RAG
    ragKpi: [
        { key: 'yAxis', label: 'Metric Value' },
        { key: 'min', label: 'Min (Optional)' },
        { key: 'max', label: 'Max (Optional)' },
        { key: 'target', label: 'Target / Threshold' }
    ],
    // 5. Sankey
    sankey: [
        { key: 'source', label: 'Source Entity' },
        { key: 'target', label: 'Target Entity' },
        { key: 'value', label: 'Flow Volume' }
    ],
    // 6. Funnel
    funnel: [
        { key: 'xAxis', label: 'Funnel Stage' },
        { key: 'yAxis', label: 'Stage Value' }
    ],
    // 7. Table
    table: [
        { key: 'columns', label: 'Columns (Dimensions & Measures)' }
    ],
    // 8. Pivot
    pivot: [
        { key: 'rows', label: 'Row Fields' },
        { key: 'columns', label: 'Column Fields' },
        { key: 'values', label: 'Value Fields' }
    ],
    // 9. Gauge
    gauge: [
        { key: 'yAxis', label: 'Current Metric' },
        { key: 'min', label: 'Min Value (Optional)' },
        { key: 'max', label: 'Max Value (Optional)' },
        { key: 'target', label: 'Target (Optional)' }
    ],
    // 10. Pie / Donut
    pie: [
        { key: 'xAxis', label: 'Category (Slice)' },
        { key: 'yAxis', label: 'Value (Size)' }
    ],
    donut: [
        { key: 'xAxis', label: 'Category (Slice)' },
        { key: 'yAxis', label: 'Value (Size)' }
    ],
    // 11. Box Plot
    boxplot: [
        { key: 'yAxis', label: 'Distribution Metric' },
        { key: 'xAxis', label: 'Group By (Dimension)' }
    ],
    // 12. Scatter
    scatter: [
        { key: 'xAxis', label: 'X Measure' },
        { key: 'yAxis', label: 'Y Measure' },
        { key: 'legend', label: 'Color / Group (Optional)' }
    ],
    // 13. Bubble
    bubble: [
        { key: 'xAxis', label: 'X Measure' },
        { key: 'yAxis', label: 'Y Measure' },
        { key: 'size', label: 'Bubble Size' },
        { key: 'legend', label: 'Color / Group (Optional)' }
    ],
    // 14. Treemap
    treemap: [
        { key: 'xAxis', label: 'Hierarchy Category' },
        { key: 'yAxis', label: 'Size Metric' },
        { key: 'color', label: 'Color Metric (Optional)' }
    ],
    // 15. Heatmap
    heatmap: [
        { key: 'xAxis', label: 'X Dimension' },
        { key: 'yAxis', label: 'Y Dimension' },
        { key: 'legend', label: 'Heat Value' }
    ],
    // 16. Line / Area
    line: [
        { key: 'xAxis', label: 'Dimension' },
        { key: 'yAxis', label: 'Measures' },
        { key: 'legend', label: 'Stack / Group' }
    ],
    area: [
        { key: 'xAxis', label: 'Dimension' },
        { key: 'yAxis', label: 'Measures' },
        { key: 'legend', label: 'Stack / Group' }
    ],
    bar: [
        { key: 'xAxis', label: 'Dimension' },
        { key: 'yAxis', label: 'Measures' },
        { key: 'legend', label: 'Stack / Group' }
    ],
    // Waterfall
    waterfall: [
        { key: 'xAxis', label: 'Dimension' },
        { key: 'yAxis', label: 'Measures' }
    ],
    optionsTitle: {
        waterfall: "Waterfall Settings",
        boxplot: "Box Plot Settings",
        sparkline: "Sparkline Settings",
        table: "Table Settings"
    },
    // 17. Combo
    combo: [
        { key: 'xAxis', label: 'Dimension' },
        { key: 'yAxis', label: 'Bar Measures' },
        { key: 'lineMeasures', label: 'Line Measures' } // Special key for combo lines
    ],
    // 18. Filter Control
    filter: [
        { key: 'column', label: 'Filter Field' }
    ]
};

const Toggle = ({ checked = false, onChange, disabled = false }) => (
    <div
        onClick={() => !disabled && onChange && onChange(!checked)}
        className={`w-8 h-4 rounded-full p-0.5 transition-colors cursor-pointer ${checked ? 'bg-blue-600' : 'bg-slate-200'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
        <div className={`w-3 h-3 rounded-full bg-white shadow-sm transition-transform ${checked ? 'translate-x-4' : 'translate-x-0'}`} />
    </div>
);

const FormatSettings = ({ value, onChange, label = "Format" }) => (
    <div className="space-y-2 border border-slate-100 rounded p-2 bg-slate-50">
        <label className="block text-[10px] font-bold text-slate-500">{label}</label>

        {/* Display Name Override */}
        <div>
            <input
                type="text"
                placeholder="Display Name Override"
                className="w-full text-xs p-1.5 border border-slate-200 rounded mb-2"
                value={value?.displayName || ''}
                onChange={(e) => onChange({ ...value, displayName: e.target.value })}
            />
        </div>

        <div className="grid grid-cols-2 gap-2">
            <select
                className="text-xs p-1.5 border border-slate-200 rounded"
                value={value?.type || 'raw'}
                onChange={(e) => onChange({ ...value, type: e.target.value })}
            >
                <option value="raw">Raw</option>
                <option value="number">Number</option>
                <option value="currency">Currency</option>
                <option value="percent">Percentage</option>
            </select>
            {/* Decimals for Number/Currency/Percent */}
            {['number', 'currency', 'percent'].includes(value?.type) && (
                <input
                    type="number"
                    min="0"
                    max="10"
                    placeholder="Decimals"
                    className="text-xs p-1.5 border border-slate-200 rounded"
                    value={value?.decimals ?? 2}
                    onChange={(e) => onChange({ ...value, decimals: Number(e.target.value) })}
                />
            )}
        </div>

        {/* Currency Symbol */}
        {value?.type === 'currency' && (
            <div className="flex items-center gap-2">
                <span className="text-[10px] text-slate-500">Symbol</span>
                <input
                    type="text"
                    className="w-12 text-xs p-1 border border-slate-200 rounded"
                    value={value?.symbol || '$'}
                    onChange={(e) => onChange({ ...value, symbol: e.target.value })}
                />
            </div>
        )}

        {/* Compact Toggle for Number/Currency */}
        {['number', 'currency'].includes(value?.type) && (
            <div className="flex items-center justify-between">
                <span className="text-[10px] text-slate-500">Compact (1k, 1M)</span>
                <Toggle
                    checked={value?.compact || false}
                    onChange={(chk) => onChange({ ...value, compact: chk })}
                />
            </div>
        )}
    </div>
);

const DataConfigView = ({ selectedWidget, dataset, activeDataModel, onWidgetUpdate, onQuickAdd, customMeasures = [], onEditMeasure = null, onDeleteMeasure = null }) => {
    // ── Active Data Model Mini-Card state ───────────────────────────────────
    const [adm_expandedTables, setAdm_expandedTables] = useState<Record<string, boolean>>({});
    const [adm_sectionsOpen, setAdm_sectionsOpen] = useState({ tables: true, measures: true });
    const [fieldSearch, setFieldSearch] = useState('');

    // Instead of activeDataModel, fallback to dataset if data model is missing
    const adm_tables: any[] = activeDataModel?.tables || [];
    // Use dataset.measures because it contains both model measures and custom measures
    const adm_measures: any[] = dataset?.measures || activeDataModel?.measures || [];
    const displayName = activeDataModel?.name || dataset?.name || 'Dataset';

    const searchLower = fieldSearch.toLowerCase();
    const filteredTables = fieldSearch ? adm_tables.map(t => {
        const tNameMatch = t.name.toLowerCase().includes(searchLower);
        const matchingCols = (t.columns || []).filter((c: any) => c.name.toLowerCase().includes(searchLower));
        if (tNameMatch || matchingCols.length > 0) {
            return { ...t, columns: tNameMatch ? t.columns : matchingCols };
        }
        return null;
    }).filter(Boolean) : adm_tables;

    const filteredMeasures = fieldSearch ? adm_measures.filter(m =>
        m.name.toLowerCase().includes(searchLower)
    ) : adm_measures;

    // 1. Dataset is passed via props now
    // 2. Configuration State - Initialize from widget config or empty
    const [config, setConfig] = useState({
        xAxis: [],
        yAxis: [],
        legend: [],
        series: [], // Ensure series is tracked for locking logic
        ...(selectedWidget?.config || {})
    });

    // CRITICAL: Reset config when widget changes OR when external update happens (e.g. Quick Add)
    useEffect(() => {
        if (selectedWidget?.config) {
            // Check if local config is actually different to avoid infinite loops
            const isDiff = JSON.stringify(config) !== JSON.stringify({ ...config, ...selectedWidget.config });

            if (isDiff || selectedWidget.id !== (config._widgetId)) {
                setConfig({
                    xAxis: [],
                    yAxis: [],
                    legend: [],
                    series: [],
                    ...selectedWidget.config,
                    _widgetId: selectedWidget.id // Track ID internally to force reset on switch
                });
            }
        }
    }, [selectedWidget?.id, selectedWidget?.config]);

    // 3. Sync Changes to Parent (Persistence)
    useEffect(() => {
        if (onWidgetUpdate && selectedWidget) {
            onWidgetUpdate(selectedWidget.id, { config });
        }
    }, [config]);

    const [aggOpen, setAggOpen] = useState(null); // {zone, index, rect}

    // 4. Drag Handlers
    const onDragStartField = (e, field, category) => {
        e.dataTransfer.setData("application/json", JSON.stringify({ field, category, type: 'new' }));
        e.dataTransfer.effectAllowed = "copy";
    };

    const onDragStartExisting = (e, zone, index, field) => {
        e.dataTransfer.setData("application/json", JSON.stringify({ field, zone, index, type: 'move' }));
        e.dataTransfer.effectAllowed = "move";
    };

    const onDrop = (e, targetZone, targetIndex = null) => {
        e.preventDefault();
        e.stopPropagation();
        try {
            const transferData = e.dataTransfer.getData("application/json");
            if (!transferData) return;

            const data = JSON.parse(transferData);
            // console.log("[WidgetToolbox] onDrop:", {targetZone, targetIndex, data});

            // Helper to get default agg
            const getValueZoneDefault = (zone, category) => {
                const valueZones = ['yAxis', 'series', 'forecast', 'confidence', 'target', 'comparison', 'value', 'size', 'min', 'max', 'lineMeasures'];
                if (valueZones.includes(zone)) {
                    return category === 'measure' ? 'Sum' : 'Count';
                }
                return '-';
            };

            if (data.type === 'new') {
                const defaultAgg = getValueZoneDefault(targetZone, data.category);
                const uniqueId = `field_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
                const newItem = {
                    ...data.field,
                    id: data.field.id || uniqueId,
                    agg: defaultAgg,
                    category: data.category
                };

                setConfig(prev => {
                    const list = [...(prev[targetZone] || [])];
                    if (targetIndex !== null) {
                        list.splice(targetIndex, 0, newItem);
                    } else {
                        list.push(newItem);
                    }
                    return { ...prev, [targetZone]: list };
                });
            } else if (data.type === 'move') {
                const { zone: sourceZone, index: sourceIndex } = data;

                setConfig(prev => {
                    // Create Shallow Copies of Lists
                    const sourceList = [...(prev[sourceZone] || [])];
                    const targetList = (sourceZone === targetZone) ? sourceList : [...(prev[targetZone] || [])];

                    // Remove from source
                    const removed = sourceList.splice(sourceIndex, 1);
                    if (removed.length === 0) return prev; // Safety check if index invalid

                    // CRITICAL: Clone object to avoid Reference mutation issues
                    let movedItem = { ...removed[0] };

                    // Calculate Insertion Index
                    let finalIndex = targetIndex;
                    if (targetIndex === null) {
                        finalIndex = targetList.length; // Append
                    } else if (sourceZone === targetZone) {
                        // Same zone: Adjust index because we removed an item
                        if (sourceIndex < targetIndex) {
                            finalIndex = targetIndex - 1;
                        }
                    }

                    // Strict Aggregation Logic
                    const valueZones = ['yAxis', 'series', 'forecast', 'confidence', 'target', 'comparison', 'value', 'size', 'min', 'max', 'lineMeasures'];
                    const isTargetValue = valueZones.includes(targetZone);

                    if (isTargetValue) {
                        // Entering Value Zone
                        if (!movedItem.agg || movedItem.agg === '-') {
                            movedItem.agg = movedItem.category === 'measure' ? 'Sum' : 'Count';
                        }
                    } else {
                        // Leaving Value Zone (or staying in non-value) -> Reset to '-'
                        movedItem.agg = '-';
                    }

                    // Insert into Target
                    targetList.splice(finalIndex, 0, movedItem);

                    return {
                        ...prev,
                        [sourceZone]: sourceList,
                        [targetZone]: targetList
                    };
                });
            }

        } catch (err) { console.error("[WidgetToolbox] Drop Error:", err); }
    };

    const updateAgg = (zone, index, newAgg) => {
        setConfig(prev => {
            const list = [...prev[zone]];
            list[index].agg = newAgg;
            return { ...prev, [zone]: list };
        });
        setAggOpen(null);
    };

    const removeField = (zone, index) => {
        setConfig(prev => {
            const list = [...prev[zone]];
            list.splice(index, 1);
            return { ...prev, [zone]: list };
        });
    };

    // Close agg dropdown on scroll/click outside
    useEffect(() => {
        const handleClickOutside = () => setAggOpen(null);
        window.addEventListener('click', handleClickOutside);
        return () => window.removeEventListener('click', handleClickOutside);
    }, []);

    const toggleAgg = (e, zone, index) => {
        e.stopPropagation(); // Stop window click
        const rect = e.currentTarget.getBoundingClientRect();
        if (aggOpen?.zone === zone && aggOpen?.index === index) {
            setAggOpen(null);
        } else {
            setAggOpen({ zone, index, rect });
        }
    };

    // Empty State Check
    if (!dataset && !activeDataModel) {
        return (
            <div className="flex flex-col items-center justify-center h-64 text-slate-400 border-2 border-dashed border-slate-200 rounded-xl m-4">
                <Database className="w-10 h-10 mb-2 opacity-30" />
                <p className="text-sm font-medium">No Dataset Selected</p>
                <p className="text-xs max-w-[200px] text-center mt-1">Please select a dataset from the Data tab to configure data.</p>
            </div>
        );
    }

    // Accordion State for Fields Tab REMOVED for independent opening
    // const [openSection, setOpenSection] = useState('dataset');

    return (
        <div className="flex flex-col gap-2 relative">

            {/* Header */}
            <div className="p-3 bg-white border border-blue-200 rounded-lg shadow-sm">
                <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Editing</div>
                </div>
                <div className="flex items-center gap-2 font-bold text-slate-800 text-sm">
                    <BarChart className="w-4 h-4 text-blue-600" />
                    {selectedWidget.type} Widget
                </div>
            </div>

            {/* ── ACTIVE DATA MODEL CARD ── */}
            <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                    <Database className="w-4 h-4 text-indigo-600" />
                    <span className="text-xs font-bold text-indigo-900 uppercase tracking-wider">Active Dataset</span>
                </div>

                {(activeDataModel || dataset) ? (
                    <>
                        {/* Model name row */}
                        <div className="flex items-center gap-2 bg-white border border-indigo-200 rounded-md p-2 shadow-sm min-h-[34px] mb-2">
                            <Database className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                            <span className="text-sm font-medium text-slate-900 truncate flex-1" title={displayName}>
                                {displayName}
                            </span>
                        </div>

                        {/* Badges */}
                        <div className="flex gap-2 mb-2">
                            <span className="flex items-center gap-1 text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-semibold">
                                <Table className="w-2.5 h-2.5" />{filteredTables.length} Tables
                            </span>
                            <span className="flex items-center gap-1 text-[10px] bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-semibold">
                                <Sigma className="w-2.5 h-2.5" />{filteredMeasures.length} Measures
                            </span>
                        </div>

                        {/* Search Bar */}
                        <div className="mb-2 relative">
                            <input
                                type="text"
                                placeholder="Search fields & tables..."
                                className="w-full text-[11px] p-1.5 pl-6 border border-indigo-200 rounded text-slate-600 focus:outline-none focus:border-indigo-400 bg-white placeholder-slate-400"
                                value={fieldSearch}
                                onChange={(e) => setFieldSearch(e.target.value)}
                            />
                            <svg className="w-3.5 h-3.5 text-slate-400 absolute left-2 top-2 mt-px" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            {fieldSearch && (
                                <button
                                    onClick={() => setFieldSearch('')}
                                    className="absolute right-2 top-1.5 mt-px text-slate-400 hover:text-slate-600"
                                >
                                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            )}
                        </div>

                        {/* Tables & Measures expandable tree */}
                        <div className="border border-slate-200 rounded-lg overflow-hidden text-[11px]">
                            {/* TABLES section */}
                            <button
                                onClick={() => setAdm_sectionsOpen(p => ({ ...p, tables: !p.tables }))}
                                className="w-full flex items-center gap-1.5 px-2 py-1.5 bg-slate-50 hover:bg-slate-100 border-b border-slate-100 transition-colors"
                            >
                                {adm_sectionsOpen.tables
                                    ? <ChevronDown className="w-3 h-3 text-slate-400" />
                                    : <ChevronRight className="w-3 h-3 text-slate-400" />
                                }
                                <Table className="w-3 h-3 text-blue-500" />
                                <span className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">Tables</span>
                                <span className="ml-auto text-[9px] text-slate-400 bg-slate-200 rounded-full px-1.5 py-0.5">{filteredTables.length}</span>
                            </button>

                            {adm_sectionsOpen.tables && (
                                <div className="bg-white divide-y divide-slate-50">
                                    {filteredTables.length === 0 && (
                                        <div className="px-3 py-2 text-[10px] text-slate-400 italic">No tables found</div>
                                    )}
                                    {filteredTables.map((t: any) => {
                                        const isExpanded = fieldSearch ? true : adm_expandedTables[t.name];
                                        return (
                                            <div key={t.id || t.name}>
                                                <button
                                                    onClick={() => !fieldSearch && setAdm_expandedTables(prev => ({ ...prev, [t.name]: !prev[t.name] }))}
                                                    className={`w-full flex items-center gap-2 px-2 py-1 transition-colors ${fieldSearch ? 'bg-blue-50/50 cursor-default' : 'hover:bg-blue-50'}`}
                                                >
                                                    {!fieldSearch && (
                                                        isExpanded
                                                            ? <ChevronDown className="w-3 h-3 text-slate-300 shrink-0" />
                                                            : <ChevronRight className="w-3 h-3 text-slate-300 shrink-0" />
                                                    )}
                                                    <Table className="w-3 h-3 text-blue-400 shrink-0 ml-[1px]" />
                                                    <span className="text-xs text-slate-700 font-medium truncate flex-1 text-left">{t.name}</span>
                                                </button>
                                                {isExpanded && (
                                                    <div className="bg-slate-50/50 pl-5 pr-2 pb-1 border-t border-slate-100">
                                                        {(t.columns || []).length === 0 && (
                                                            <div className="text-[10px] text-slate-400 italic px-2 py-1">No columns</div>
                                                        )}
                                                        {(t.columns || []).map((col: any, i: number) => {
                                                            const isNumCol = /int|float|decimal|numeric|double|real/.test((col.data_type || '').toLowerCase());
                                                            const isDateCol = /date|time|timestamp/.test((col.data_type || '').toLowerCase());
                                                            const colCategory = isNumCol ? 'measure' : 'dimension';
                                                            const fieldObj = {
                                                                name: `${t.name}.${col.name}`,
                                                                type: isNumCol ? 'number' : isDateCol ? 'date' : 'text',
                                                                table: t.name,
                                                            };
                                                            return (
                                                                <div
                                                                    key={col.name || i}
                                                                    draggable
                                                                    onDragStart={(e) => onDragStartField(e, fieldObj, colCategory)}
                                                                    className="flex items-center gap-1.5 px-2 py-1 text-[11px] text-slate-600 my-0.5 hover:bg-white hover:text-blue-600 cursor-grab active:cursor-grabbing border border-transparent hover:border-slate-200 rounded transition-all group"
                                                                >
                                                                    <span className={`text-[8px] font-bold uppercase px-1 py-0.5 rounded min-w-[24px] text-center ${isNumCol ? 'bg-orange-100 text-orange-600'
                                                                        : isDateCol ? 'bg-green-100 text-green-600'
                                                                            : 'bg-slate-100 text-slate-500'
                                                                        }`}>
                                                                        {col.data_type ? col.data_type.slice(0, 3).toUpperCase() : 'STR'}
                                                                    </span>
                                                                    <span className="truncate flex-1">{col.name}</span>
                                                                    {onQuickAdd && (
                                                                        <button
                                                                            onClick={(e) => { e.stopPropagation(); onQuickAdd(fieldObj, colCategory); }}
                                                                            className="opacity-0 group-hover:opacity-100 p-0.5 text-slate-400 hover:text-blue-600 rounded bg-white shadow-sm border border-slate-100 transition-opacity"
                                                                            title="Add to chart"
                                                                        >
                                                                            <Plus className="w-3 h-3" />
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    })}
                                </div>
                            )}

                            {/* MEASURES section */}
                            <button
                                onClick={() => setAdm_sectionsOpen(p => ({ ...p, measures: !p.measures }))}
                                className="w-full flex items-center gap-1.5 px-2 py-1.5 bg-slate-50 hover:bg-slate-100 border-t border-slate-200 transition-colors"
                            >
                                {adm_sectionsOpen.measures
                                    ? <ChevronDown className="w-3 h-3 text-slate-400" />
                                    : <ChevronRight className="w-3 h-3 text-slate-400" />
                                }
                                <Sigma className="w-3 h-3 text-orange-500" />
                                <span className="text-[10px] font-bold text-slate-600 uppercase tracking-wider">Measures</span>
                                <span className="ml-auto text-[9px] text-slate-400 bg-slate-200 rounded-full px-1.5 py-0.5">{filteredMeasures.length}</span>
                            </button>

                            {adm_sectionsOpen.measures && (
                                <div className="bg-white divide-y divide-slate-50">
                                    {filteredMeasures.length === 0 && (
                                        <div className="px-3 py-2 text-[10px] text-slate-400 italic">No measures found</div>
                                    )}
                                    {filteredMeasures.map((m: any) => {
                                        const mField = {
                                            name: m.name,
                                            type: 'number',
                                            expression: m.expression,
                                        };
                                        const rawCustom = customMeasures.find((cm: any) => cm.name === m.name);
                                        const isCustom = !!rawCustom;
                                        return (
                                            <div
                                                key={m.id || m.name}
                                                draggable
                                                onDragStart={(e) => onDragStartField(e, mField, 'measure')}
                                                className="flex items-center gap-2 px-3 py-1.5 hover:bg-orange-50 cursor-grab active:cursor-grabbing transition-colors group"
                                            >
                                                <Sigma className="w-3 h-3 text-orange-400 shrink-0" />
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-xs text-slate-700 font-medium truncate">{m.name}</div>
                                                    {m.expression && (
                                                        <div className="text-[9px] text-slate-400 truncate font-mono mt-0.5">{m.expression}</div>
                                                    )}
                                                </div>

                                                {isCustom && onEditMeasure && (
                                                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); onEditMeasure(rawCustom); }}
                                                            className="p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                                                            title="Edit Measure"
                                                        >
                                                            <Edit2 className="w-3.5 h-3.5" />
                                                        </button>
                                                        {onDeleteMeasure && (
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    onDeleteMeasure(rawCustom);
                                                                }}
                                                                className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                                                                title="Delete Measure"
                                                            >
                                                                <Trash2 className="w-3.5 h-3.5" />
                                                            </button>
                                                        )}
                                                    </div>
                                                )}

                                                {onQuickAdd && !isCustom && (
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); onQuickAdd(mField, 'measure'); }}
                                                        className="opacity-0 group-hover:opacity-100 p-0.5 text-slate-400 hover:text-orange-600 rounded bg-white shadow-sm border border-slate-100 transition-opacity"
                                                        title="Add to chart"
                                                    >
                                                        <Plus className="w-3 h-3" />
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                    <div className="text-[10px] text-slate-400 italic text-center py-2">
                        No data model selected — go to the <span className="font-bold">Data</span> tab to choose one.
                    </div>
                )}
            </div>



            {/* 2. FIELDS ACCORDION */}
            <Accordion
                title="Data Fields"
                defaultOpen={true}
            >

                {/* Helper for Fields */}
                {(CHART_ZONE_CONFIG[selectedWidget.type] || CHART_ZONE_CONFIG.bar).map(zoneCfg => {
                    const zone = zoneCfg.key;
                    const label = zoneCfg.label;

                    return (
                        <div
                            key={zone}
                            onDragOver={(e) => { e.preventDefault(); }}
                            onDrop={(e) => onDrop(e, zone)} // Container drop maps to end of list
                            className="space-y-1"
                        >
                            <div className="text-[10px] font-bold text-slate-400 uppercase">{label}</div>
                            <div className={`p-2 min-h-[40px] border-2 border-dashed rounded-lg transition-colors ${config[zone]?.length > 0 ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-200 hover:border-blue-300'}`}>
                                {config[zone]?.length === 0 || !config[zone] ? (
                                    <div className="text-[10px] text-slate-400 text-center py-1">Drop here</div>
                                ) : (
                                    <div className="flex flex-col gap-1">
                                        {config[zone].map((item, idx) => (
                                            <div
                                                key={`${item.name}-${idx}`}
                                                className="flex items-center justify-between text-xs p-1.5 bg-slate-100 rounded border border-slate-200 group relative cursor-move"
                                                draggable
                                                onDragStart={(e) => onDragStartExisting(e, zone, idx, item)}
                                                onDragOver={(e) => { e.stopPropagation(); e.preventDefault(); }}
                                                onDrop={(e) => { e.stopPropagation(); onDrop(e, zone, idx); }}
                                            >
                                                <div className="flex items-center gap-1.5 truncate pointer-events-none">
                                                    {item.category === 'measure' ? <Sigma className="w-3 h-3 text-orange-400" /> : <Table className="w-3 h-3 text-blue-300" />}
                                                    <span className="font-medium text-slate-700">{item.name}</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    {/* Aggregation Trigger */}
                                                    <button
                                                        onClick={(e) => toggleAgg(e, zone, idx)}
                                                        className={`text-[10px] px-1.5 py-0.5 rounded uppercase tracking-tight transition-colors shadow-sm ${(!item.agg || item.agg === '-') ? 'bg-transparent text-slate-400 hover:text-blue-600' : 'bg-white border border-slate-300 text-slate-600 hover:text-blue-600 hover:border-blue-300'}`}
                                                    >
                                                        {(!item.agg || item.agg === '-') ? '-' : item.agg}
                                                    </button>
                                                    <button onClick={() => removeField(zone, idx)} className="text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-white rounded">
                                                        <Trash2 className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </Accordion>

            {aggOpen && createPortal(
                (() => {
                    const spaceBelow = window.innerHeight - aggOpen.rect.bottom;
                    const showAbove = spaceBelow < 250; // Threshold for menu height
                    return (
                        <div
                            className="fixed z-[9999] bg-white border border-slate-200 rounded-lg shadow-xl flex flex-col py-1 animate-in fade-in zoom-in-95 duration-100"
                            style={{
                                top: showAbove ? undefined : aggOpen.rect.bottom + 4,
                                bottom: showAbove ? (window.innerHeight - aggOpen.rect.top + 4) : undefined,
                                left: aggOpen.rect.left - 100 + aggOpen.rect.width, // Align Right
                                minWidth: '120px'
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="px-3 py-1.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100 mb-1">
                                Aggregation
                            </div>
                            {['-', 'Sum', 'Count', 'Avg', 'Min', 'Max', 'Distinct'].map(agg => (
                                <button
                                    key={agg}
                                    onClick={() => updateAgg(aggOpen.zone, aggOpen.index, agg)}
                                    className="text-left text-xs px-4 py-2 hover:bg-blue-50 text-slate-700 hover:text-blue-700 transition-colors"
                                >
                                    {agg === '-' ? 'None (-)' : agg}
                                </button>
                            ))}
                        </div>
                    );
                })(),
                document.body
            )}

            {/* 3. QUERY OPTIONS ACCORDION */}
            <Accordion
                title="Query and Sort Options"
                defaultOpen={false}
            >
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-600">Top N Records</span>
                        <input
                            type="number"
                            className="w-16 text-xs p-1 border border-slate-200 rounded"
                            placeholder="All"
                            value={config.query_config?.limit || ''}
                            onChange={(e) => setConfig(prev => ({
                                ...prev,
                                query_config: { ...prev.query_config, limit: e.target.value ? Number(e.target.value) : null }
                            }))}
                        />
                    </div>

                    {/* Sort By - Hide for Table charts (they have their own sorting in Format tab) */}
                    {selectedWidget.type !== 'table' && (
                        <div className="space-y-2">
                            <span className="text-xs text-slate-600 block">Sort By</span>
                            <div className="flex gap-2">
                                <select
                                    className="flex-1 text-xs p-1 border border-slate-200 rounded bg-white"
                                    value={config.query_config?.sortBy || 'default'}
                                    onChange={(e) => setConfig(prev => ({
                                        ...prev,
                                        query_config: { ...prev.query_config, sortBy: e.target.value }
                                    }))}
                                >
                                    <option value="default">Default</option>
                                    {/* Populate from selected Dimension/Measures */}
                                    {[...(config.xAxis || []), ...(config.yAxis || [])].map(f => (
                                        <option key={f.name} value={f.name}>{f.name}</option>
                                    ))}
                                </select>
                                {/* Hide Direction if Default */}
                                {(config.query_config?.sortBy && config.query_config.sortBy !== 'default') && (
                                    <select
                                        className="w-20 text-xs p-1 border border-slate-200 rounded bg-white"
                                        value={config.query_config?.sortDir || 'asc'}
                                        onChange={(e) => setConfig(prev => ({
                                            ...prev,
                                            query_config: { ...prev.query_config, sortDir: e.target.value }
                                        }))}
                                    >
                                        <option value="asc">Asc</option>
                                        <option value="desc">Desc</option>
                                    </select>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Explicit Date Format - For Smart Sort Precision */}
                    <div className="space-y-2">
                        <label className="text-xs text-slate-600 block">
                            Source Data Date Format <span className="text-[10px] text-slate-400 font-normal">(Optional)</span>
                        </label>
                        <input
                            key={`${selectedWidget.id}-dateFormat`}
                            type="text"
                            placeholder="e.g. %m/%d/%Y"
                            className="w-full text-xs p-1.5 border border-slate-200 rounded focus:border-blue-500 outline-none"
                            defaultValue={config.query_config?.sourceDateFormat || ''}
                            onBlur={(e) => {
                                const newVal = e.target.value;
                                if (newVal !== config.query_config?.sourceDateFormat) {
                                    setConfig(prev => ({
                                        ...prev,
                                        query_config: { ...prev.query_config, sourceDateFormat: newVal }
                                    }));
                                }
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.currentTarget.blur();
                                }
                            }}
                        />
                        <p className="text-[10px] text-slate-400">
                            Helps interpret manual dates correctly.
                        </p>
                    </div>

                    <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-600">Cross-Filtering</span>
                        <Toggle
                            checked={config.query_config?.crossFilter !== false} // Default True
                            onChange={(chk) => setConfig(prev => ({
                                ...prev,
                                query_config: { ...prev.query_config, crossFilter: chk }
                            }))}
                        />
                    </div>

                    <div className="flex items-center justify-between opacity-50 cursor-not-allowed">
                        <span className="text-xs text-slate-600">Drill-Through</span>
                        <Toggle disabled={true} onChange={() => { }} />
                    </div>

                    {/* AI Insights Toggle */}
                    <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                        <div className="flex items-center gap-2">
                            <Sparkles className="w-3 h-3 text-purple-500" />
                            <span className="text-xs text-slate-600 font-medium">AI Insights</span>
                        </div>
                        <Toggle
                            checked={!!config.aiInsights}
                            onChange={(chk) => setConfig(prev => ({ ...prev, aiInsights: chk }))}
                        />
                    </div>
                </div>
            </Accordion>
        </div >
    );
};

/* 3. FORMAT CONFIG VIEW (Restored & Interactive) */
const FormatConfigView = ({ widget, dataset, onWidgetUpdate }) => {
    // Inclusive Accordion State
    const [openSection, setOpenSection] = useState('chart'); // 'chart', 'xaxis', 'yaxis', 'legend', 'datalabels', 'tooltip', 'plot', 'widget', 'general'
    // Helper to update style deeply
    const updateStyle = (key, value) => {
        const currentStyle = widget.config?.style || {};
        const newStyle = { ...currentStyle, [key]: value };

        onWidgetUpdate(widget.id, {
            config: {
                ...widget.config,
                style: newStyle
            }
        });
    };

    // Helper to update specific nested style objects (e.g. style.xAxis.show)
    const updateNestedStyle = (parent, key, value) => {
        const currentStyle = widget.config?.style || {};
        const parentObj = currentStyle[parent] || {};
        const newParentObj = { ...parentObj, [key]: value };

        onWidgetUpdate(widget.id, {
            config: {
                ...widget.config,
                style: { ...currentStyle, [parent]: newParentObj }
            }
        });
    };

    const s = widget.config?.style || {};
    const ySeries = [
        ...(widget.config?.yAxis || []),
        ...((widget.type === 'combo' ? widget.config?.lineMeasures : []) || [])
    ].map(y => ({
        id: y.name || y.field,
        name: y.name || y.field,
    }));


    return (
        <div className="flex flex-col gap-2">
            {/* PAGE HEADER CONFIG */}
            {widget.type === 'pageHeader' && (
                <Accordion
                    title="Header Visuals"
                    icon={<Layout className="w-3 h-3 text-indigo-500" />}

                    isOpen={openSection === 'headerVisuals'}
                    onToggle={() => setOpenSection(openSection === 'headerVisuals' ? null : 'headerVisuals')}
                >
                    <div className="space-y-4">
                        {/* Title Section */}
                        <div className="space-y-2">
                            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">Title Settings</label>
                            <input
                                type="text"
                                className="w-full text-xs p-2 border border-slate-200 rounded focus:border-blue-500 outline-none"
                                value={widget.config?.title || ''}
                                onChange={(e) => onWidgetUpdate(widget.id, { config: { ...widget.config, title: e.target.value } })}
                                placeholder="Dashboard Title"
                            />
                            {/* Title Styling */}
                            <div className="grid grid-cols-2 gap-2">
                                {/* Font Size */}
                                <div>
                                    <label className="text-[9px] text-slate-400">Size</label>
                                    <input
                                        type="number"
                                        className="w-full text-xs p-1 border border-slate-200 rounded"
                                        value={widget.config?.style?.title?.fontSize || 24}
                                        onChange={(e) => updateNestedStyle('title', 'fontSize', Number(e.target.value))}
                                    />
                                </div>
                                {/* Color */}
                                <div>
                                    <label className="text-[9px] text-slate-400">Color</label>
                                    <div className="flex items-center gap-1 border border-slate-200 rounded p-1 bg-white">
                                        <input
                                            type="color"
                                            className="w-4 h-4 p-0 border-0 cursor-pointer"
                                            value={widget.config?.style?.title?.color || '#1e293b'}
                                            onChange={(e) => updateNestedStyle('title', 'color', e.target.value)}
                                        />
                                        <span className="text-[10px] text-slate-500">{widget.config?.style?.title?.color || '#1e293b'}</span>
                                    </div>
                                </div>
                            </div>
                            {/* Title Rich Text & Alignment */}
                            <div className="flex gap-2">
                                <div className="flex border border-slate-200 rounded p-0.5 bg-slate-50">
                                    <button onClick={() => updateNestedStyle('title', 'bold', !widget.config?.style?.title?.bold)} className={`px-2 py-0.5 text-[10px] rounded ${widget.config?.style?.title?.bold ? 'bg-white shadow text-blue-600 font-bold' : 'text-slate-500'}`}>B</button>
                                    <button onClick={() => updateNestedStyle('title', 'italic', !widget.config?.style?.title?.italic)} className={`px-2 py-0.5 text-[10px] rounded ${widget.config?.style?.title?.italic ? 'bg-white shadow text-blue-600 italic' : 'text-slate-500'}`}>I</button>
                                    <button onClick={() => updateNestedStyle('title', 'underline', !widget.config?.style?.title?.underline)} className={`px-2 py-0.5 text-[10px] rounded ${widget.config?.style?.title?.underline ? 'bg-white shadow text-blue-600 underline' : 'text-slate-500'}`}>U</button>
                                </div>
                                <div className="flex border border-slate-200 rounded p-0.5 bg-slate-50 ml-auto">
                                    {['left', 'center', 'right'].map(align => (
                                        <button key={align} onClick={() => updateNestedStyle('title', 'align', align)} className={`px-2 py-0.5 text-[10px] rounded capitalize ${widget.config?.style?.title?.align === align ? 'bg-white shadow text-blue-600 font-bold' : 'text-slate-500'}`}>{align.charAt(0).toUpperCase()}</button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Subtitle Section */}
                        <div className="space-y-2 pt-4 border-t border-slate-100">
                            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">Subtitle Settings</label>
                            <input
                                type="text"
                                className="w-full text-xs p-2 border border-slate-200 rounded focus:border-blue-500 outline-none"
                                value={widget.config?.description || ''}
                                onChange={(e) => onWidgetUpdate(widget.id, { config: { ...widget.config, description: e.target.value } })}
                                placeholder="Subtitle text..."
                            />
                            {/* Subtitle Styling */}
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="text-[9px] text-slate-400">Size</label>
                                    <input
                                        type="number"
                                        className="w-full text-xs p-1 border border-slate-200 rounded"
                                        value={widget.config?.style?.description?.fontSize || 14}
                                        onChange={(e) => updateNestedStyle('description', 'fontSize', Number(e.target.value))}
                                    />
                                </div>
                                <div>
                                    <label className="text-[9px] text-slate-400">Color</label>
                                    <div className="flex items-center gap-1 border border-slate-200 rounded p-1 bg-white">
                                        <input
                                            type="color"
                                            className="w-4 h-4 p-0 border-0 cursor-pointer"
                                            value={widget.config?.style?.description?.color || '#64748b'}
                                            onChange={(e) => updateNestedStyle('description', 'color', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                            {/* Subtitle Rich Text & Alignment */}
                            <div className="flex gap-2">
                                <div className="flex border border-slate-200 rounded p-0.5 bg-slate-50">
                                    <button onClick={() => updateNestedStyle('description', 'bold', !widget.config?.style?.description?.bold)} className={`px-2 py-0.5 text-[10px] rounded ${widget.config?.style?.description?.bold ? 'bg-white shadow text-blue-600 font-bold' : 'text-slate-500'}`}>B</button>
                                    <button onClick={() => updateNestedStyle('description', 'italic', !widget.config?.style?.description?.italic)} className={`px-2 py-0.5 text-[10px] rounded ${widget.config?.style?.description?.italic ? 'bg-white shadow text-blue-600 italic' : 'text-slate-500'}`}>I</button>
                                    <button onClick={() => updateNestedStyle('description', 'underline', !widget.config?.style?.description?.underline)} className={`px-2 py-0.5 text-[10px] rounded ${widget.config?.style?.description?.underline ? 'bg-white shadow text-blue-600 underline' : 'text-slate-500'}`}>U</button>
                                </div>
                                <div className="flex border border-slate-200 rounded p-0.5 bg-slate-50 ml-auto">
                                    {['left', 'center', 'right'].map(align => (
                                        <button key={align} onClick={() => updateNestedStyle('description', 'align', align)} className={`px-2 py-0.5 text-[10px] rounded capitalize ${widget.config?.style?.description?.align === align ? 'bg-white shadow text-blue-600 font-bold' : 'text-slate-500'}`}>{align.charAt(0).toUpperCase()}</button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Logo Section */}
                        <div className="space-y-2 pt-4 border-t border-slate-100">
                            <div className="flex items-center justify-between">
                                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">Logo Settings</label>
                                <Toggle
                                    checked={widget.config?.showLogo !== false}
                                    onChange={(chk) => onWidgetUpdate(widget.id, { config: { ...widget.config, showLogo: chk } })}
                                />
                            </div>

                            {widget.config?.showLogo !== false && (
                                <div className="space-y-3">
                                    {/* Placement */}
                                    <div>
                                        <label className="text-[9px] text-slate-400 mb-1 block">Placement</label>
                                        <div className="flex border border-slate-200 rounded p-0.5 bg-slate-50">
                                            <button
                                                onClick={() => onWidgetUpdate(widget.id, { config: { ...widget.config, logoPlacement: 'left' } })}
                                                className={`flex-1 py-1 text-[10px] rounded ${widget.config?.logoPlacement !== 'right' ? 'bg-white shadow text-blue-600 font-bold' : 'text-slate-500'}`}
                                            >
                                                Left
                                            </button>
                                            <button
                                                onClick={() => onWidgetUpdate(widget.id, { config: { ...widget.config, logoPlacement: 'right' } })}
                                                className={`flex-1 py-1 text-[10px] rounded ${widget.config?.logoPlacement === 'right' ? 'bg-white shadow text-blue-600 font-bold' : 'text-slate-500'}`}
                                            >
                                                Right
                                            </button>
                                        </div>
                                    </div>

                                    {/* Upload */}
                                    <div>
                                        <label className="text-[9px] text-slate-400 mb-1 block">Upload Image</label>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="block w-full text-xs text-slate-500
                                              file:mr-2 file:py-1 file:px-2
                                              file:rounded-full file:border-0
                                              file:text-xs file:font-semibold
                                              file:bg-blue-50 file:text-blue-700
                                              hover:file:bg-blue-100"
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file) {
                                                    const reader = new FileReader();
                                                    reader.onloadend = () => {
                                                        onWidgetUpdate(widget.id, { config: { ...widget.config, logoUrl: reader.result } });
                                                    };
                                                    reader.readAsDataURL(file);
                                                }
                                            }}
                                        />
                                    </div>

                                    {/* URL Fallback */}
                                    <input
                                        type="text"
                                        placeholder="Or enter Image URL..."
                                        className="w-full text-xs p-2 border border-slate-200 rounded focus:border-blue-500 outline-none"
                                        value={widget.config?.logoUrl || ''} // Can handle Base64 too
                                        onChange={(e) => onWidgetUpdate(widget.id, { config: { ...widget.config, logoUrl: e.target.value } })}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Background */}
                        <div className="pt-2 border-t border-slate-100 flex items-center justify-between">
                            <label className="text-[10px] font-bold text-slate-500">Background</label>
                            <div className="flex items-center gap-2">
                                <input
                                    type="color"
                                    className="w-5 h-5 p-0 border-0 rounded cursor-pointer"
                                    value={widget.config?.style?.backgroundColor || '#ffffff'}
                                    onChange={(e) => updateStyle('backgroundColor', e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                </Accordion>
            )}
            {/* 1. GENERAL (Text Widgets) */}
            <Accordion
                title="General"
                isOpen={openSection === 'general'}
                onToggle={() => setOpenSection(openSection === 'general' ? null : 'general')}
            >
                <div className="space-y-3">
                    {/* TEXT WIDGET CONTENT EDITOR */}
                    {widget.type === 'text' && (
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 mb-1">Text Content (HTML Supported)</label>
                            <textarea
                                className="w-full text-xs p-2 border border-slate-200 rounded focus:border-blue-500 outline-none min-h-[100px] bg-white font-mono"
                                placeholder="Enter text or HTML..."
                                value={widget.config?.text || ''}
                                onChange={(e) => onWidgetUpdate(widget.id, { config: { ...widget.config, text: e.target.value } })}
                            />
                        </div>
                    )}
                    {/* Widget Title Toggle */}
                    <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-600">Show Widget Title</span>
                        <Toggle
                            checked={s.showTitle !== false}
                            onChange={(chk) => updateStyle('showTitle', chk)}
                        />
                    </div>

                    {s.showTitle !== false && (
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 mb-1">Title Text</label>
                            <input
                                type="text"
                                className="w-full text-xs p-1.5 border border-slate-200 rounded focus:border-blue-500 outline-none"
                                placeholder="Widget Title"
                                value={widget.config?.title || ''}
                                onChange={(e) => onWidgetUpdate(widget.id, { config: { ...widget.config, title: e.target.value } })}
                            />
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 mb-1">Font Size</label>
                            <input
                                type="number"
                                className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                value={s.fontSize || 14}
                                onChange={(e) => updateStyle('fontSize', Number(e.target.value))}
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 mb-1">Alignment</label>
                            <div className="flex border border-slate-200 rounded p-0.5 bg-slate-50">
                                {['left', 'center', 'right'].map(align => (
                                    <button
                                        key={align}
                                        onClick={() => updateStyle('textAlign', align)}
                                        className={`flex-1 rounded text-xs py-1 text-center capitalize transition-all ${s.textAlign === align ? 'bg-white shadow-sm font-bold text-blue-600' : 'hover:bg-white hover:shadow-sm text-slate-500'}`}
                                    >
                                        {align === 'left' ? 'L' : align === 'center' ? 'C' : 'R'}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <div className="flex-1">
                            <label className="block text-[10px] font-bold text-slate-400 mb-1">Font Color</label>
                            <div className="flex items-center gap-2">
                                <input
                                    type="color"
                                    className="w-6 h-6 p-0 border-0 rounded cursor-pointer"
                                    value={s.color || '#334155'}
                                    onChange={(e) => updateStyle('color', e.target.value)}
                                />
                                <span className="text-xs text-slate-500">{s.color || '#334155'}</span>
                            </div>
                        </div>
                        <div className="flex-1 flex items-end gap-1">
                            <button
                                onClick={() => updateStyle('fontWeight', s.fontWeight === 'bold' ? 'normal' : 'bold')}
                                className={`flex-1 border border-slate-200 rounded p-1.5 text-xs font-bold transition-colors ${s.fontWeight === 'bold' ? 'bg-blue-50 text-blue-600 border-blue-200' : 'hover:bg-slate-50 text-slate-600'}`}
                            >
                                B
                            </button>
                            <button
                                onClick={() => updateStyle('fontStyle', s.fontStyle === 'italic' ? 'normal' : 'italic')}
                                className={`flex-1 border border-slate-200 rounded p-1.5 text-xs italic transition-colors ${s.fontStyle === 'italic' ? 'bg-blue-50 text-blue-600 border-blue-200' : 'hover:bg-slate-50 text-slate-600'}`}
                            >
                                I
                            </button>
                        </div>
                    </div>

                    <div className="space-y-2 pt-2 border-t border-slate-100">
                        <div className="flex items-center justify-between">
                            <span className="text-xs text-slate-600">Title Bar Background</span>
                            <div className="flex items-center gap-2">
                                <input
                                    type="color"
                                    className="w-4 h-4 p-0 border-0 rounded cursor-pointer"
                                    value={s.backgroundColor || '#ffffff'}
                                    onChange={(e) => updateStyle('backgroundColor', e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                </div >
            </Accordion >

            {/* 1.5 CHART SPECIFIC (Dynamic) */}
            {['bar', 'line', 'area', 'scatter', 'combo', 'pie', 'donut', 'bubble', 'heatmap', 'sankey', 'funnel', 'treemap', 'gauge', 'kpi', 'filter', 'waterfall', 'boxplot', 'sparkline', 'table'].includes(widget.type) && widget.type !== 'pageHeader' && (
                <Accordion
                    title={widget.type === 'filter' ? 'Filter Settings' : (CHART_ZONE_CONFIG.optionsTitle?.[widget.type] || 'Chart Specifics')}
                    icon={<BarChart className="w-3 h-3 text-blue-500" />}
                    isOpen={openSection === 'chart'}
                    onToggle={() => setOpenSection(openSection === 'chart' ? null : 'chart')}
                >
                    <div className="space-y-3">
                        {/* BAR / COMBO SETTINGS */}
                        {['bar', 'combo'].includes(widget.type) && (
                            <>
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-slate-600">Orientation</span>
                                    <div className="flex border border-slate-200 rounded p-0.5 bg-slate-50">
                                        <button
                                            onClick={() => updateNestedStyle('bar', 'horizontal', false)}
                                            className={`px-2 py-0.5 text-[10px] rounded ${!s.bar?.horizontal ? 'bg-white shadow-sm text-blue-600 font-bold' : 'text-slate-500'}`}
                                        >
                                            Vertical
                                        </button>
                                        <button
                                            onClick={() => updateNestedStyle('bar', 'horizontal', true)}
                                            className={`px-2 py-0.5 text-[10px] rounded ${s.bar?.horizontal ? 'bg-white shadow-sm text-blue-600 font-bold' : 'text-slate-500'}`}
                                        >
                                            Horizontal
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-400 mb-1">Stacking</label>
                                    <select
                                        className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                        value={s.bar?.stacked ? (s.bar?.stackType === '100%' ? '100%' : 'normal') : 'none'}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            const isStacked = val !== 'none';
                                            const type = val === '100%' ? '100%' : 'normal';

                                            // Atomic update to prevent closure race condition
                                            onWidgetUpdate(widget.id, {
                                                config: {
                                                    ...widget.config,
                                                    style: {
                                                        ...(widget.config?.style || {}),
                                                        bar: {
                                                            ...(widget.config?.style?.bar || {}),
                                                            stacked: isStacked,
                                                            stackType: isStacked ? type : 'normal'
                                                        }
                                                    }
                                                }
                                            });
                                        }}
                                    >
                                        <option value="none">Grouped (Side-by-Side)</option>
                                        <option value="normal">Stacked (Standard)</option>
                                        <option value="100%">Stacked (100%)</option>
                                    </select>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-400 mb-1">Bar Width</label>
                                        <select
                                            className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                            value={s.bar?.barWidth || 'auto'}
                                            onChange={(e) => updateNestedStyle('bar', 'barWidth', e.target.value)}
                                        >
                                            <option value="auto">Auto</option>
                                            <option value="20%">Thin</option>
                                            <option value="50%">Medium</option>
                                            <option value="80%">Thick</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-400 mb-1">Corner Radius</label>
                                        <input
                                            type="number" max="20"
                                            className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                            value={s.bar?.borderRadius || 0}
                                            onChange={(e) => updateNestedStyle('bar', 'borderRadius', Number(e.target.value))}
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        {/* LINE / AREA SETTINGS */}
                        {['line', 'area', 'combo'].includes(widget.type) && (
                            <>
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-400 mb-1">Stacking</label>
                                    <select
                                        className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                        value={s.line?.stacked ? (s.line?.stackType === '100%' ? '100%' : 'normal') : 'none'}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            const isStacked = val !== 'none';
                                            const type = val === '100%' ? '100%' : 'normal';

                                            // Atomic update
                                            onWidgetUpdate(widget.id, {
                                                config: {
                                                    ...widget.config,
                                                    style: {
                                                        ...s,
                                                        line: {
                                                            ...s.line,
                                                            stacked: isStacked,
                                                            stackType: type
                                                        }
                                                    }
                                                }
                                            });
                                        }}
                                    >
                                        <option value="none">None (Overlaid)</option>
                                        <option value="normal">Stacked (Standard)</option>
                                        <option value="100%">Stacked (100%)</option>
                                    </select>
                                </div>

                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-slate-600">Fill Area</span>
                                    <Toggle
                                        checked={s.line?.area || widget.type === 'area'}
                                        onChange={(chk) => updateNestedStyle('line', 'area', chk)}
                                    />
                                </div>

                                <div>
                                    <label className="block text-[10px] font-bold text-slate-400 mb-1">Line Style</label>
                                    <div className="flex border border-slate-200 rounded p-0.5 bg-slate-50">
                                        {['smooth', 'linear', 'step'].map(lv => (
                                            <button
                                                key={lv}
                                                onClick={() => updateNestedStyle('line', 'smooth', lv === 'smooth' ? true : (lv === 'step' ? 'step' : false))}
                                                className={`flex-1 py-0.5 text-[10px] rounded capitalize ${(lv === 'smooth' && s.line?.smooth === true) ||
                                                    (lv === 'step' && s.line?.smooth === 'step') ||
                                                    (lv === 'linear' && !s.line?.smooth)
                                                    ? 'bg-white shadow-sm text-blue-600 font-bold' : 'text-slate-500'
                                                    }`}
                                            >
                                                {lv}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-slate-600">Show Points</span>
                                    <Toggle
                                        checked={s.line?.showSymbol !== false}
                                        onChange={(chk) => updateNestedStyle('line', 'showSymbol', chk)}
                                    />
                                </div>
                                {s.line?.showSymbol !== false && (
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-400 mb-1">Point Size: {s.line?.symbolSize ?? 4}</label>
                                        <input
                                            type="range" min="2" max="20"
                                            className="w-full h-1 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                                            value={s.line?.symbolSize ?? 4}
                                            onChange={(e) => updateNestedStyle('line', 'symbolSize', Number(e.target.value))}
                                        />
                                    </div>
                                )}
                                {(widget.type === 'area' || widget.type === 'combo' || s.line?.area) && (
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-400 mb-1">Area Opacity: {s.area?.opacity ?? 0.3}</label>
                                        <input
                                            type="range" min="0" max="1" step="0.1"
                                            className="w-full h-1 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                                            value={s.area?.opacity ?? 0.3}
                                            onChange={(e) => updateNestedStyle('area', 'opacity', Number(e.target.value))}
                                        />
                                    </div>
                                )}
                            </>
                        )}

                        {/* DATA ZOOM (Cartesian) */}
                        {['bar', 'line', 'area', 'scatter', 'combo'].includes(widget.type) && (
                            <div className="flex items-center justify-between pt-2 border-t border-slate-100 mt-2">
                                <span className="text-xs text-slate-600">Data Zoom Slider</span>
                                <Toggle
                                    checked={!!s.dataZoom}
                                    onChange={(chk) => updateStyle('dataZoom', chk)}
                                />
                            </div>
                        )}

                        {/* KPI SETTINGS */}
                        {widget.type === 'kpi' && (
                            <div className="space-y-4">
                                {/* Metric Name Styling */}
                                <div className="space-y-2">
                                    <span className="text-xs font-bold text-slate-600 block">Metric Name</span>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-400 mb-1">Font Size</label>
                                            <input
                                                type="number"
                                                className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                                value={s.kpi?.name?.fontSize || 12}
                                                onChange={(e) => updateNestedStyle('kpi', 'name', { ...s.kpi?.name, fontSize: Number(e.target.value) })}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-400 mb-1">Color</label>
                                            <input
                                                type="color"
                                                className="w-full h-[30px] rounded border border-slate-200 cursor-pointer"
                                                value={s.kpi?.name?.color || '#64748b'}
                                                onChange={(e) => updateNestedStyle('kpi', 'name', { ...s.kpi?.name, color: e.target.value })}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-400 mb-1">Alignment</label>
                                            <select
                                                className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                                value={s.kpi?.align || 'center'} // Global align for now
                                                onChange={(e) => updateNestedStyle('kpi', 'align', e.target.value)}
                                            >
                                                <option value="left">Left</option>
                                                <option value="center">Center</option>
                                                <option value="right">Right</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => updateNestedStyle('kpi', 'name', { ...s.kpi?.name, bold: !s.kpi?.name?.bold })}
                                            className={`flex-1 border border-slate-200 rounded p-1 text-xs font-bold transition-colors ${s.kpi?.name?.bold ? 'bg-blue-50 text-blue-600 border-blue-200' : 'hover:bg-slate-50'}`}
                                        >
                                            B
                                        </button>
                                        <button
                                            onClick={() => updateNestedStyle('kpi', 'name', { ...s.kpi?.name, italic: !s.kpi?.name?.italic })}
                                            className={`flex-1 border border-slate-200 rounded p-1 text-xs italic transition-colors ${s.kpi?.name?.italic ? 'bg-blue-50 text-blue-600 border-blue-200' : 'hover:bg-slate-50'}`}
                                        >
                                            I
                                        </button>
                                    </div>
                                </div>

                                <div className="border-t border-slate-100"></div>

                                {/* Metric Value Styling */}
                                <div className="space-y-2">
                                    <span className="text-xs font-bold text-slate-600 block">Metric Value</span>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-400 mb-1">Font Size</label>
                                            <input
                                                type="number"
                                                className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                                value={s.kpi?.value?.fontSize || 60}
                                                onChange={(e) => updateNestedStyle('kpi', 'value', { ...s.kpi?.value, fontSize: Number(e.target.value) })}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-400 mb-1">Color</label>
                                            <input
                                                type="color"
                                                className="w-full h-[30px] rounded border border-slate-200 cursor-pointer"
                                                value={s.kpi?.value?.color || '#1e293b'}
                                                onChange={(e) => updateNestedStyle('kpi', 'value', { ...s.kpi?.value, color: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => updateNestedStyle('kpi', 'value', { ...s.kpi?.value, bold: !s.kpi?.value || s.kpi?.value?.bold !== false })} // Default bold
                                            className={`flex-1 border border-slate-200 rounded p-1 text-xs font-bold transition-colors ${s.kpi?.value?.bold !== false ? 'bg-blue-50 text-blue-600 border-blue-200' : 'hover:bg-slate-50'}`}
                                        >
                                            B
                                        </button>
                                        <button
                                            onClick={() => updateNestedStyle('kpi', 'value', { ...s.kpi?.value, italic: !s.kpi?.value?.italic })}
                                            className={`flex-1 border border-slate-200 rounded p-1 text-xs italic transition-colors ${s.kpi?.value?.italic ? 'bg-blue-50 text-blue-600 border-blue-200' : 'hover:bg-slate-50'}`}
                                        >
                                            I
                                        </button>
                                    </div>
                                </div>

                                <div className="border-t border-slate-100"></div>

                                {/* Trend Styling */}
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs text-slate-600">Show Trend Icon</span>
                                        <Toggle
                                            checked={s.kpi?.showTrend !== false}
                                            onChange={(chk) => updateNestedStyle('kpi', 'showTrend', chk)}
                                        />
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs text-slate-600">Trend Logic</span>
                                        <select
                                            className="text-xs p-1 border border-slate-200 rounded"
                                            value={s.kpi?.trendColor || 'normal'}
                                            onChange={(e) => updateNestedStyle('kpi', 'trendColor', e.target.value)}
                                        >
                                            <option value="normal">Normal (Green Up)</option>
                                            <option value="inverse">Inverse (Red Up)</option>
                                            <option value="neutral">Neutral (Blue)</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* RAG SETTINGS */}
                        {widget.type === 'ragKpi' && (
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-slate-600">Sentiment</span>
                                    <select
                                        className="text-xs p-1 border border-slate-200 rounded"
                                        value={s.rag?.sentiment || 'normal'}
                                        onChange={(e) => updateNestedStyle('rag', 'sentiment', e.target.value)}
                                    >
                                        <option value="normal">Normal (Low=Bad, High=Good)</option>
                                        <option value="inverse">Inverse (Low=Good, High=Bad)</option>
                                    </select>
                                </div>
                                <div className="border-t border-slate-100 pt-2 mt-2"></div>
                                <span className="text-xs font-bold text-slate-500 block mb-2">Threshold Colors</span>
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-slate-600">Critical</span>
                                    <input
                                        type="color"
                                        className="w-6 h-6 p-0 border border-slate-200 rounded cursor-pointer"
                                        value={s.rag?.criticalColor || '#ef4444'}
                                        onChange={(e) => updateNestedStyle('rag', 'criticalColor', e.target.value)}
                                    />
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-slate-600">Warning</span>
                                    <input
                                        type="color"
                                        className="w-6 h-6 p-0 border border-slate-200 rounded cursor-pointer"
                                        value={s.rag?.warningColor || '#f59e0b'}
                                        onChange={(e) => updateNestedStyle('rag', 'warningColor', e.target.value)}
                                    />
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-slate-600">Healthy</span>
                                    <input
                                        type="color"
                                        className="w-6 h-6 p-0 border border-slate-200 rounded cursor-pointer"
                                        value={s.rag?.healthyColor || '#22c55e'}
                                        onChange={(e) => updateNestedStyle('rag', 'healthyColor', e.target.value)}
                                    />
                                </div>
                            </div>
                        )}

                        {/* GAUGE SETTINGS */}
                        {widget.type === 'gauge' && (
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-slate-600">Progress Bar</span>
                                    <Toggle
                                        checked={!!s.gauge?.progressBar}
                                        onChange={(chk) => updateNestedStyle('gauge', 'progressBar', chk)}
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-400 mb-1">Axis Width: {s.gauge?.axisWidth ?? 10}</label>
                                    <input
                                        type="range" min="5" max="30"
                                        className="w-full h-1 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                                        value={s.gauge?.axisWidth ?? 10}
                                        onChange={(e) => updateNestedStyle('gauge', 'axisWidth', Number(e.target.value))}
                                    />
                                </div>
                            </div>
                        )}

                        {/* SANKEY SETTINGS */}
                        {widget.type === 'sankey' && (
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-slate-600">Orientation</span>
                                    <select
                                        className="text-xs p-1 border border-slate-200 rounded"
                                        value={s.sankey?.orient || 'horizontal'}
                                        onChange={(e) => updateNestedStyle('sankey', 'orient', e.target.value)}
                                    >
                                        <option value="horizontal">Horizontal</option>
                                        <option value="vertical">Vertical</option>
                                    </select>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-slate-600">Node Align</span>
                                    <select
                                        className="text-xs p-1 border border-slate-200 rounded"
                                        value={s.sankey?.align || 'justify'}
                                        onChange={(e) => updateNestedStyle('sankey', 'align', e.target.value)}
                                    >
                                        <option value="justify">Justify</option>
                                        <option value="left">Left</option>
                                        <option value="right">Right</option>
                                    </select>
                                </div>
                            </div>
                        )}

                        {/* TREEMAP SETTINGS */}
                        {widget.type === 'treemap' && (
                            <div className="space-y-2">
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-400 mb-1">Visible Depth</label>
                                    <input
                                        type="number" min="1" max="5"
                                        className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                        value={s.treemap?.leafDepth || 1}
                                        onChange={(e) => updateNestedStyle('treemap', 'leafDepth', Number(e.target.value))}
                                    />
                                </div>
                            </div>
                        )}

                        {/* BOXPLOT SETTINGS */}
                        {widget.type === 'boxplot' && (
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-slate-600">Horizontal</span>
                                    <Toggle
                                        checked={!!s.boxplot?.horizontal}
                                        onChange={(chk) => updateNestedStyle('boxplot', 'horizontal', chk)}
                                    />
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-slate-600">Show Outliers</span>
                                    <Toggle
                                        checked={s.boxplot?.showOutliers !== false}
                                        onChange={(chk) => updateNestedStyle('boxplot', 'showOutliers', chk)}
                                    />
                                </div>
                                {s.boxplot?.showOutliers !== false && (
                                    <div className="flex items-center justify-between">
                                        <span className="text-[10px] font-bold text-slate-400">Outlier Color</span>
                                        <input
                                            type="color"
                                            className="w-6 h-6 p-0 border border-slate-200 rounded cursor-pointer"
                                            value={s.boxplot?.outlierColor || s.colors?.[0] || '#5470c6'}
                                            onChange={(e) => updateNestedStyle('boxplot', 'outlierColor', e.target.value)}
                                        />
                                    </div>
                                )}
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-400 mb-1">Box Width: {s.boxplot?.boxWidth ?? 50}%</label>
                                    <input
                                        type="range" min="20" max="100"
                                        className="w-full h-1 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                                        value={s.boxplot?.boxWidth ?? 50}
                                        onChange={(e) => updateNestedStyle('boxplot', 'boxWidth', Number(e.target.value))}
                                    />
                                </div>
                            </div>
                        )}

                        {/* TABLE SETTINGS */}
                        {widget.type === 'table' && (
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-slate-600">Striped Rows</span>
                                    <Toggle
                                        checked={s.table?.striped !== false}
                                        onChange={(chk) => updateNestedStyle('table', 'striped', chk)}
                                    />
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-slate-600">Show Borders</span>
                                    <Toggle
                                        checked={s.table?.showBorders !== false}
                                        onChange={(chk) => updateNestedStyle('table', 'showBorders', chk)}
                                    />
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-slate-600">Compact Mode</span>
                                    <Toggle
                                        checked={!!s.table?.compact}
                                        onChange={(chk) => updateNestedStyle('table', 'compact', chk)}
                                    />
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-slate-600">Enable Sorting</span>
                                    <Toggle
                                        checked={s.table?.enableSorting !== false}
                                        onChange={(chk) => updateNestedStyle('table', 'enableSorting', chk)}
                                    />
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-slate-600">Enable Search</span>
                                    <Toggle
                                        checked={!!s.table?.enableSearch}
                                        onChange={(chk) => updateNestedStyle('table', 'enableSearch', chk)}
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-400 mb-1">Default Sort Column</label>
                                    <select
                                        className="w-full text-xs p-1.5 border border-slate-200 rounded bg-white"
                                        value={s.table?.defaultSortColumn || ''}
                                        onChange={(e) => updateNestedStyle('table', 'defaultSortColumn', e.target.value)}
                                    >
                                        <option value="">None</option>
                                        {(widget.config?.columns || []).map(col => {
                                            const colName = typeof col === 'string' ? col : col?.name;
                                            return <option key={colName} value={colName}>{colName}</option>;
                                        })}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-400 mb-1">Page Size</label>
                                    <select
                                        className="w-full text-xs p-1.5 border border-slate-200 rounded bg-white"
                                        value={s.table?.pageSize || 10}
                                        onChange={(e) => updateNestedStyle('table', 'pageSize', Number(e.target.value))}
                                    >
                                        <option value={5}>5 rows</option>
                                        <option value={10}>10 rows</option>
                                        <option value={25}>25 rows</option>
                                        <option value={50}>50 rows</option>
                                        <option value={100}>100 rows</option>
                                    </select>
                                </div>
                            </div>
                        )}

                        {/* SPARKLINE SETTINGS */}
                        {widget.type === 'sparkline' && (
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-slate-600">Area Fill</span>
                                    <Toggle
                                        checked={s.sparkline?.area !== false}
                                        onChange={(chk) => updateNestedStyle('sparkline', 'area', chk)}
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-400 mb-1">Marker Color</label>
                                    <input
                                        type="color"
                                        className="w-full h-6 p-0 border-0 rounded cursor-pointer"
                                        value={s.sparkline?.markerColor || '#ff0000'}
                                        onChange={(e) => updateNestedStyle('sparkline', 'markerColor', e.target.value)}
                                    />
                                </div>
                            </div>
                        )}

                        {/* FILTER WIDGET SETTINGS */}
                        {widget.type === 'filter' && (
                            <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-400 mb-1">Margin Top (px)</label>
                                        <input
                                            type="number"
                                            className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                            value={s.filter?.marginTop || 0}
                                            onChange={(e) => updateNestedStyle('filter', 'marginTop', Number(e.target.value))}
                                            min={0}
                                            max={100}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-400 mb-1">Margin Left (px)</label>
                                        <input
                                            type="number"
                                            className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                            value={s.filter?.marginLeft || 0}
                                            onChange={(e) => updateNestedStyle('filter', 'marginLeft', Number(e.target.value))}
                                            min={0}
                                            max={100}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-400 mb-1">Background</label>
                                    <input
                                        type="color"
                                        className="w-full h-6 p-0 border-0 rounded cursor-pointer"
                                        value={s.filter?.backgroundColor || '#ffffff'}
                                        onChange={(e) => updateNestedStyle('filter', 'backgroundColor', e.target.value)}
                                    />
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-slate-600">Show Border</span>
                                    <input
                                        type="checkbox"
                                        checked={s.filter?.showBorder !== false}
                                        onChange={(e) => updateNestedStyle('filter', 'showBorder', e.target.checked)}
                                        className="w-4 h-4"
                                    />
                                </div>
                                {s.filter?.showBorder !== false && (
                                    <div>
                                        <label className="block text-[10px] font-bold text-slate-400 mb-1">Border Color</label>
                                        <input
                                            type="color"
                                            className="w-full h-6 p-0 border border-slate-200 rounded cursor-pointer"
                                            value={s.filter?.borderColor || '#e2e8f0'}
                                            onChange={(e) => updateNestedStyle('filter', 'borderColor', e.target.value)}
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        {/* PIE / DONUT SETTINGS */}
                        {['pie', 'donut'].includes(widget.type) && (
                            <>
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-slate-600">Rose Type</span>
                                    <select
                                        className="text-xs p-1 border border-slate-200 rounded"
                                        value={s.pie?.roseType || 'false'}
                                        onChange={(e) => updateNestedStyle('pie', 'roseType', e.target.value === 'false' ? false : e.target.value)}
                                    >
                                        <option value="false">None</option>
                                        <option value="radius">Radius</option>
                                        <option value="area">Area</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-400 mb-1">Inner Radius: {s.pie?.innerRadius ?? (widget.type === 'donut' ? 60 : 0)}%</label>
                                    <input
                                        type="range" min="0" max="90"
                                        className="w-full h-1 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                                        value={s.pie?.innerRadius ?? (widget.type === 'donut' ? 60 : 0)}
                                        onChange={(e) => updateNestedStyle('pie', 'innerRadius', Number(e.target.value))}
                                    />
                                </div>
                            </>
                        )}

                        {/* BUBBLE SETTINGS */}
                        {widget.type === 'bubble' && (
                            <div>
                                <label className="block text-[10px] font-bold text-slate-400 mb-1">Max Bubble Size: {s.bubble?.maxSize ?? 50}px</label>
                                <input
                                    type="range" min="10" max="100"
                                    className="w-full h-1 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                                    value={s.bubble?.maxSize ?? 50}
                                    onChange={(e) => updateNestedStyle('bubble', 'maxSize', Number(e.target.value))}
                                />
                            </div>
                        )}

                        {/* HEATMAP SETTINGS */}
                        {widget.type === 'heatmap' && (
                            <div className="flex items-center justify-between">
                                <span className="text-xs text-slate-600">Show Values</span>
                                <Toggle
                                    checked={s.heatmap?.label?.show !== false}
                                    onChange={(chk) => updateNestedStyle('heatmap', 'label', { show: chk })}
                                />
                            </div>
                        )}

                        {/* FUNNEL SETTINGS */}
                        {widget.type === 'funnel' && (
                            <div className="flex items-center justify-between">
                                <span className="text-xs text-slate-600">Sort</span>
                                <select
                                    className="text-xs p-1 border border-slate-200 rounded"
                                    value={s.funnel?.sort || 'descending'}
                                    onChange={(e) => updateNestedStyle('funnel', 'sort', e.target.value)}
                                >
                                    <option value="descending">Descending</option>
                                    <option value="ascending">Ascending</option>
                                    <option value="none">None</option>
                                </select>
                            </div>
                        )}

                        {/* WATERFALL SETTINGS */}
                        {widget.type === 'waterfall' && (
                            <div className="space-y-3 pt-1">
                                {/* Orientation & Direction */}
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-slate-600">Horizontal</span>
                                    <Toggle
                                        checked={!!s.waterfall?.horizontal}
                                        onChange={(chk) => updateNestedStyle('waterfall', 'horizontal', chk)}
                                    />
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-slate-600">Ascending (Reversed)</span>
                                    <Toggle
                                        checked={!!s.waterfall?.ascending}
                                        onChange={(chk) => updateNestedStyle('waterfall', 'ascending', chk)}
                                    />
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-slate-600">Show Total Bar</span>
                                    <Toggle
                                        checked={s.waterfall?.showTotal !== false}
                                        onChange={(chk) => updateNestedStyle('waterfall', 'showTotal', chk)}
                                    />
                                </div>

                                <div className="border-t border-slate-100 pt-2 mt-2"></div>
                                <span className="text-xs font-bold text-slate-500 block mb-2">Step Colors</span>

                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-slate-600">Start / Total</span>
                                    <input
                                        type="color"
                                        className="w-6 h-6 p-0 border border-slate-200 rounded cursor-pointer"
                                        value={s.waterfall?.totalColor || '#5470c6'}
                                        onChange={(e) => updateNestedStyle('waterfall', 'totalColor', e.target.value)}
                                    />
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-slate-600">Increase</span>
                                    <input
                                        type="color"
                                        className="w-6 h-6 p-0 border border-slate-200 rounded cursor-pointer"
                                        value={s.waterfall?.increaseColor || '#91cc75'}
                                        onChange={(e) => updateNestedStyle('waterfall', 'increaseColor', e.target.value)}
                                    />
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-slate-600">Decrease</span>
                                    <input
                                        type="color"
                                        className="w-6 h-6 p-0 border border-slate-200 rounded cursor-pointer"
                                        value={s.waterfall?.decreaseColor || '#ef4444'}
                                        onChange={(e) => updateNestedStyle('waterfall', 'decreaseColor', e.target.value)}
                                    />
                                </div>
                            </div>
                        )}

                        {/* ─────────────────────────────────────────────────── */}
                        {/* SERIES / BAR COLORS  (all chart types except table/filter/text/image) */}
                        {!['table', 'filter', 'text', 'image', 'pageHeader', 'kpi', 'ragKpi'].includes(widget.type) && (
                            <>
                                <div className="border-t border-slate-100 pt-3 mt-1">
                                    <div className="flex items-center gap-1.5 mb-2">
                                        <Palette className="w-3 h-3 text-violet-500" />
                                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Series Colors</span>
                                        {s.colors && (
                                            <button
                                                onClick={() => updateStyle('colors', undefined)}
                                                className="ml-auto text-[9px] text-slate-400 hover:text-red-500 bg-slate-100 hover:bg-red-50 px-1.5 py-0.5 rounded border border-slate-200 transition-colors"
                                                title="Reset to default palette"
                                            >
                                                Reset
                                            </button>
                                        )}
                                    </div>

                                    {/* Preset Palette Chips */}
                                    <div className="mb-3">
                                        <label className="block text-[9px] text-slate-400 mb-1.5">Preset Palettes</label>
                                        <div className="flex flex-col gap-1.5">
                                            {Object.entries(COLOR_PALETTES).map(([paletteName, colors]) => (
                                                <button
                                                    key={paletteName}
                                                    onClick={() => updateStyle('colors', [...colors])}
                                                    className={`flex items-center gap-2 w-full px-2 py-1 rounded border transition-all text-left ${JSON.stringify(s.colors) === JSON.stringify(colors)
                                                        ? 'border-violet-400 bg-violet-50 ring-1 ring-violet-200'
                                                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                                                        }`}
                                                    title={`Apply ${paletteName} palette`}
                                                >
                                                    {/* Color swatches */}
                                                    <div className="flex gap-0.5 shrink-0">
                                                        {(colors as string[]).slice(0, 7).map((c: string, i: number) => (
                                                            <span
                                                                key={i}
                                                                className="w-3 h-3 rounded-sm"
                                                                style={{ backgroundColor: c }}
                                                            />
                                                        ))}
                                                    </div>
                                                    <span className="text-[10px] text-slate-600 capitalize truncate">{paletteName}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Per-Series Color Pickers */}
                                    {ySeries.length > 0 && (
                                        <div>
                                            <label className="block text-[9px] text-slate-400 mb-1.5">Per-Series Colors</label>
                                            <div className="space-y-1.5">
                                                {ySeries.map((series, idx) => {
                                                    const currentColors = s.colors || Object.values(COLOR_PALETTES)[0] as string[];
                                                    const seriesColor = currentColors[idx % currentColors.length] || '#5470c6';
                                                    return (
                                                        <div key={series.id || idx} className="flex items-center justify-between gap-2">
                                                            <span className="text-[10px] text-slate-600 truncate flex-1" title={series.name}>
                                                                {series.name || `Series ${idx + 1}`}
                                                            </span>
                                                            <div className="flex items-center gap-1 shrink-0">
                                                                <input
                                                                    type="color"
                                                                    className="w-6 h-6 p-0 border border-slate-200 rounded cursor-pointer"
                                                                    value={seriesColor}
                                                                    onChange={(e) => {
                                                                        const base = s.colors
                                                                            ? [...s.colors]
                                                                            : [...(Object.values(COLOR_PALETTES)[0] as string[])];
                                                                        // Extend array if needed
                                                                        while (base.length <= idx) base.push('#5470c6');
                                                                        base[idx] = e.target.value;
                                                                        updateStyle('colors', base);
                                                                    }}
                                                                />
                                                                <span className="text-[9px] text-slate-400 font-mono w-14">{seriesColor}</span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* Hint when no series are added */}
                                    {ySeries.length === 0 && (
                                        <p className="text-[9px] text-slate-400 italic">
                                            Add measures to the Fields tab to configure individual series colors.
                                        </p>
                                    )}
                                </div>
                            </>
                        )}
                    </div >
                </Accordion >
            )
            }

            {/* COLUMN FORMATTING (Table Charts Only) */}
            {widget.type === 'table' && widget.config?.columns?.length > 0 && (
                <Accordion
                    title="Column Formatting"
                    icon={<Type className="w-3 h-3 text-blue-500" />}
                    isOpen={openSection === 'columnformatting'}
                    onToggle={() => setOpenSection(openSection === 'columnformatting' ? null : 'columnformatting')}
                >
                    <div className="space-y-2">
                        {(widget.config?.columns || []).map((col, idx) => {
                            const colName = typeof col === 'string' ? col : col?.name;
                            const colFormat = s.columnFormats?.[colName] || {};

                            return (
                                <details key={colName} className="group border border-slate-200 rounded-lg overflow-hidden">
                                    <summary className="flex items-center justify-between px-3 py-2 bg-slate-50 cursor-pointer text-xs font-medium text-slate-700 hover:bg-slate-100">
                                        <span className="truncate">{colFormat.alias || colName}</span>
                                        <ChevronDown className="w-3 h-3 text-slate-400 group-open:rotate-180 transition-transform" />
                                    </summary>
                                    <div className="p-3 space-y-3 bg-white">
                                        {/* Column Alias */}
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-400 mb-1">Column Alias</label>
                                            <input
                                                type="text"
                                                className="w-full text-xs p-1.5 border border-slate-200 rounded bg-white"
                                                placeholder={colName}
                                                value={colFormat.alias || ''}
                                                onChange={(e) => {
                                                    const newFormats = { ...s.columnFormats, [colName]: { ...colFormat, alias: e.target.value } };
                                                    updateStyle('columnFormats', newFormats);
                                                }}
                                            />
                                        </div>

                                        {/* Format Type */}
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-400 mb-1">Format</label>
                                            <select
                                                className="w-full text-xs p-1.5 border border-slate-200 rounded bg-white"
                                                value={colFormat.format || 'text'}
                                                onChange={(e) => {
                                                    const newFormats = { ...s.columnFormats, [colName]: { ...colFormat, format: e.target.value } };
                                                    updateStyle('columnFormats', newFormats);
                                                }}
                                            >
                                                <option value="text">Raw / Text</option>
                                                <option value="number">Number</option>
                                                <option value="currency">Currency</option>
                                                <option value="percentage">Percentage</option>
                                                <option value="url">URL / Link</option>
                                            </select>
                                        </div>

                                        {/* Column Alignment */}
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-400 mb-1">Alignment</label>
                                            <div className="flex border border-slate-200 rounded p-0.5 bg-slate-50">
                                                <button
                                                    onClick={() => {
                                                        const newFormats = { ...s.columnFormats, [colName]: { ...colFormat, align: 'left' } };
                                                        updateStyle('columnFormats', newFormats);
                                                    }}
                                                    className={`flex-1 px-2 py-1 text-[10px] rounded ${(colFormat.align || 'left') === 'left' ? 'bg-white shadow-sm text-blue-600 font-bold' : 'text-slate-500'}`}
                                                >
                                                    Left
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        const newFormats = { ...s.columnFormats, [colName]: { ...colFormat, align: 'center' } };
                                                        updateStyle('columnFormats', newFormats);
                                                    }}
                                                    className={`flex-1 px-2 py-1 text-[10px] rounded ${colFormat.align === 'center' ? 'bg-white shadow-sm text-blue-600 font-bold' : 'text-slate-500'}`}
                                                >
                                                    Center
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        const newFormats = { ...s.columnFormats, [colName]: { ...colFormat, align: 'right' } };
                                                        updateStyle('columnFormats', newFormats);
                                                    }}
                                                    className={`flex-1 px-2 py-1 text-[10px] rounded ${colFormat.align === 'right' ? 'bg-white shadow-sm text-blue-600 font-bold' : 'text-slate-500'}`}
                                                >
                                                    Right
                                                </button>
                                            </div>
                                        </div>

                                        {['number', 'currency', 'percentage'].includes(colFormat.format) && (
                                            <div className="space-y-2 pl-2 border-l-2 border-blue-200">
                                                <div className="grid grid-cols-2 gap-2">
                                                    <div>
                                                        <label className="block text-[10px] font-bold text-slate-400 mb-1">Decimals</label>
                                                        <input
                                                            type="number" min="0" max="6"
                                                            className="w-full text-xs p-1.5 border border-slate-200 rounded bg-white"
                                                            value={colFormat.decimals ?? 2}
                                                            onChange={(e) => {
                                                                const newFormats = { ...s.columnFormats, [colName]: { ...colFormat, decimals: Number(e.target.value) } };
                                                                updateStyle('columnFormats', newFormats);
                                                            }}
                                                        />
                                                    </div>
                                                    {colFormat.format !== 'percentage' && (
                                                        <div>
                                                            <label className="block text-[10px] font-bold text-slate-400 mb-1">Prefix</label>
                                                            <input
                                                                type="text"
                                                                className="w-full text-xs p-1.5 border border-slate-200 rounded bg-white"
                                                                placeholder={colFormat.format === 'currency' ? '$' : ''}
                                                                value={colFormat.prefix ?? (colFormat.format === 'currency' ? '$' : '')}
                                                                onChange={(e) => {
                                                                    const newFormats = { ...s.columnFormats, [colName]: { ...colFormat, prefix: e.target.value } };
                                                                    updateStyle('columnFormats', newFormats);
                                                                }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                                {colFormat.format !== 'percentage' && (
                                                    <div>
                                                        <label className="block text-[10px] font-bold text-slate-400 mb-1">Suffix</label>
                                                        <input
                                                            type="text"
                                                            className="w-full text-xs p-1.5 border border-slate-200 rounded bg-white"
                                                            placeholder=""
                                                            value={colFormat.suffix || ''}
                                                            onChange={(e) => {
                                                                const newFormats = { ...s.columnFormats, [colName]: { ...colFormat, suffix: e.target.value } };
                                                                updateStyle('columnFormats', newFormats);
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* URL/Link Options */}
                                        {colFormat.format === 'url' && (
                                            <div className="space-y-2 pl-2 border-l-2 border-blue-200">
                                                <div>
                                                    <label className="block text-[10px] font-bold text-slate-400 mb-1">Link Label (Alias)</label>
                                                    <input
                                                        type="text"
                                                        className="w-full text-xs p-1.5 border border-slate-200 rounded bg-white"
                                                        placeholder="Click here"
                                                        value={colFormat.linkLabel || ''}
                                                        onChange={(e) => {
                                                            const newFormats = { ...s.columnFormats, [colName]: { ...colFormat, linkLabel: e.target.value } };
                                                            updateStyle('columnFormats', newFormats);
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </details>
                            );
                        })}
                    </div>
                </Accordion>
            )}

            {/* 1.6 PIVOT OPTIONS (Custom) */}
            {widget.type === 'pivot' && (
                <Accordion
                    title="Pivot Options"
                    icon={<Grid className="w-3 h-3 text-blue-500" />}
                    isOpen={openSection === 'pivotOptions'}
                    onToggle={() => setOpenSection(openSection === 'pivotOptions' ? null : 'pivotOptions')}
                >
                    <div className="space-y-4">
                        {/* A. ROW HEADERS */}
                        <div className="space-y-2">
                            <span className="text-xs font-bold text-slate-600 block border-b border-slate-100 pb-1">Row Headers</span>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-400 mb-1">Font Size</label>
                                    <input type="number" className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                        value={s.pivot?.row?.fontSize || 12}
                                        onChange={(e) => updateNestedStyle('pivot', 'row', { ...s.pivot?.row, fontSize: Number(e.target.value) })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-400 mb-1">Color</label>
                                    <input type="color" className="w-full h-7 border border-slate-200 rounded cursor-pointer p-0"
                                        value={s.pivot?.row?.color || '#1e293b'}
                                        onChange={(e) => updateNestedStyle('pivot', 'row', { ...s.pivot?.row, color: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <button onClick={() => updateNestedStyle('pivot', 'row', { ...s.pivot?.row, bold: !s.pivot?.row?.bold })}
                                    className={`flex-1 border border-slate-200 rounded p-1 text-xs font-bold ${s.pivot?.row?.bold ? 'bg-blue-50 text-blue-600' : 'text-slate-500'}`}>B</button>
                                <button onClick={() => updateNestedStyle('pivot', 'row', { ...s.pivot?.row, italic: !s.pivot?.row?.italic })}
                                    className={`flex-1 border border-slate-200 rounded p-1 text-xs italic ${s.pivot?.row?.italic ? 'bg-blue-50 text-blue-600' : 'text-slate-500'}`}>I</button>
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-slate-400 mb-1">Date Format</label>
                                <select className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                    value={s.pivot?.row?.dateFormat || 'raw'}
                                    onChange={(e) => updateNestedStyle('pivot', 'row', { ...s.pivot?.row, dateFormat: e.target.value })}
                                >
                                    <option value="raw">Raw / Text</option>
                                    <option value="custom">Custom Pattern</option>
                                </select>
                                {s.pivot?.row?.dateFormat === 'custom' && (
                                    <input type="text" className="w-full text-xs p-1.5 border border-slate-200 rounded mt-1" placeholder="e.g. dd-MMM-yyyy"
                                        value={s.pivot?.row?.datePattern || ''}
                                        onChange={(e) => updateNestedStyle('pivot', 'row', { ...s.pivot?.row, datePattern: e.target.value })}
                                    />
                                )}
                            </div>
                        </div>

                        {/* B. COLUMN HEADERS */}
                        <div className="space-y-2 pt-2">
                            <span className="text-xs font-bold text-slate-600 block border-b border-slate-100 pb-1">Column Headers</span>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-400 mb-1">Font Size</label>
                                    <input type="number" className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                        value={s.pivot?.col?.fontSize || 12}
                                        onChange={(e) => updateNestedStyle('pivot', 'col', { ...s.pivot?.col, fontSize: Number(e.target.value) })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-400 mb-1">Color</label>
                                    <input type="color" className="w-full h-7 border border-slate-200 rounded cursor-pointer p-0"
                                        value={s.pivot?.col?.color || '#1e293b'}
                                        onChange={(e) => updateNestedStyle('pivot', 'col', { ...s.pivot?.col, color: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <button onClick={() => updateNestedStyle('pivot', 'col', { ...s.pivot?.col, bold: !s.pivot?.col?.bold })}
                                    className={`flex-1 border border-slate-200 rounded p-1 text-xs font-bold ${s.pivot?.col?.bold ? 'bg-blue-50 text-blue-600' : 'text-slate-500'}`}>B</button>
                                <button onClick={() => updateNestedStyle('pivot', 'col', { ...s.pivot?.col, italic: !s.pivot?.col?.italic })}
                                    className={`flex-1 border border-slate-200 rounded p-1 text-xs italic ${s.pivot?.col?.italic ? 'bg-blue-50 text-blue-600' : 'text-slate-500'}`}>I</button>
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-slate-400 mb-1">Date Format</label>
                                <select className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                    value={s.pivot?.col?.dateFormat || 'raw'}
                                    onChange={(e) => updateNestedStyle('pivot', 'col', { ...s.pivot?.col, dateFormat: e.target.value })}
                                >
                                    <option value="raw">Raw / Text</option>
                                    <option value="custom">Custom Pattern</option>
                                </select>
                                {s.pivot?.col?.dateFormat === 'custom' && (
                                    <input type="text" className="w-full text-xs p-1.5 border border-slate-200 rounded mt-1" placeholder="e.g. dd-MMM-yyyy"
                                        value={s.pivot?.col?.datePattern || ''}
                                        onChange={(e) => updateNestedStyle('pivot', 'col', { ...s.pivot?.col, datePattern: e.target.value })}
                                    />
                                )}
                            </div>
                        </div>

                        {/* C. DATA CELLS */}
                        <div className="space-y-2 pt-2">
                            <span className="text-xs font-bold text-slate-600 block border-b border-slate-100 pb-1">Data Cells</span>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-400 mb-1">Format</label>
                                    <select className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                        value={s.pivot?.cell?.format || 'number'}
                                        onChange={(e) => updateNestedStyle('pivot', 'cell', { ...s.pivot?.cell, format: e.target.value })}
                                    >
                                        <option value="text">Text</option>
                                        <option value="number">Number</option>
                                        <option value="currency">Currency</option>
                                        <option value="percentage">Percentage</option>
                                        <option value="compact">Compact (1k)</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-400 mb-1">Decimals</label>
                                    <input type="number" className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                        value={s.pivot?.cell?.decimals ?? 2}
                                        onChange={(e) => updateNestedStyle('pivot', 'cell', { ...s.pivot?.cell, decimals: Number(e.target.value) })}
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-400 mb-1">Align</label>
                                    <div className="flex border border-slate-200 rounded p-0.5 bg-slate-50">
                                        {['left', 'center', 'right'].map(a => (
                                            <button key={a} onClick={() => updateNestedStyle('pivot', 'cell', { ...s.pivot?.cell, align: a })}
                                                className={`flex-1 text-[8px] py-1 rounded capitalize ${s.pivot?.cell?.align === a ? 'bg-white shadow text-blue-600 font-bold' : 'text-slate-400'}`}>
                                                {a[0].toUpperCase()}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-400 mb-1">Font Size</label>
                                    <input type="number" className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                        value={s.pivot?.cell?.fontSize || 12}
                                        onChange={(e) => updateNestedStyle('pivot', 'cell', { ...s.pivot?.cell, fontSize: Number(e.target.value) })}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </Accordion>
            )}

            {/* 2. X-AXIS (Hidden for non-cartesian widgets) */}
            {!['filter', 'table', 'pivot', 'text', 'kpi', 'gauge', 'pie', 'donut', 'funnel', 'treemap', 'sankey', 'ragKpi', 'sparkline'].includes(widget.type) && <Accordion
                title={s.bar?.horizontal ? "Vertical Axis (Category)" : "X-Axis"}
                icon={<ArrowRight className="w-3 h-3 text-blue-500" />}
                isOpen={openSection === 'xaxis'}
                onToggle={() => setOpenSection(openSection === 'xaxis' ? null : 'xaxis')}
            >
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-600">Show Axis</span>
                        <Toggle
                            checked={s.xAxis?.show !== false}
                            onChange={(chk) => updateNestedStyle('xAxis', 'show', chk)}
                        />
                    </div>
                    <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-600">Gridlines</span>
                        <Toggle
                            checked={s.xAxis?.showGrid !== false}
                            onChange={(chk) => updateNestedStyle('xAxis', 'showGrid', chk)}
                        />
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-slate-600">Title</span>
                            <Toggle
                                checked={!!s.xAxis?.showTitle}
                                onChange={(chk) => updateNestedStyle('xAxis', 'showTitle', chk)}
                            />
                        </div>
                        {s.xAxis?.showTitle && (
                            <input
                                type="text"
                                className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                placeholder="Axis Title"
                                value={s.xAxis?.titleText || ''}
                                onChange={(e) => updateNestedStyle('xAxis', 'titleText', e.target.value)}
                            />
                        )}
                    </div>

                    {/* Date Formatting for Dimension (X-Axis) */}
                    <div className="pt-2 border-t border-slate-100 space-y-2">
                        <div className="flex items-center justify-between">
                            <span className="text-xs text-slate-600">Date Format</span>
                        </div>
                        <select
                            className="w-full text-xs p-1.5 border border-slate-200 rounded"
                            value={(widget.config?.data?.dateFormat || widget.data?.dateFormat) === 'custom' ? 'custom' : 'raw'}
                            onChange={(e) => onWidgetUpdate(widget.id, {
                                config: {
                                    ...widget.config,
                                    data: {
                                        ...(widget.config?.data || widget.data || {}),
                                        dateFormat: e.target.value
                                    }
                                }
                            })}
                        >
                            <option value="raw">Raw (Original)</option>
                            <option value="custom">Custom Pattern</option>
                        </select>

                        {/* Custom Pattern Input */}
                        {((widget.config?.data?.dateFormat || widget.data?.dateFormat) === 'custom') && (
                            <div>
                                <input
                                    type="text"
                                    className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                    placeholder="e.g. dd-MMM-yyyy"
                                    value={widget.config?.data?.datePattern || widget.data?.datePattern || ''}
                                    onChange={(e) => onWidgetUpdate(widget.id, {
                                        config: {
                                            ...widget.config,
                                            data: {
                                                ...(widget.config?.data || widget.data || {}),
                                                datePattern: e.target.value
                                            }
                                        }
                                    })}
                                />
                                <p className="text-[10px] text-slate-400 mt-1">
                                    Use d, m, y. Intelligent handling for case (mm vs MM).
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 mb-1">Label Angle</label>
                            <input
                                type="number" min="-90" max="90"
                                className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                value={s.xAxis?.labelRotation || 0}
                                onChange={(e) => updateNestedStyle('xAxis', 'labelRotation', Number(e.target.value))}
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 mb-1">Font Size</label>
                            <input
                                type="number"
                                className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                value={s.xAxis?.fontSize || 10}
                                onChange={(e) => updateNestedStyle('xAxis', 'fontSize', Number(e.target.value))}
                            />
                        </div>
                    </div>

                    <div className="flex gap-2">
                        <button
                            onClick={() => updateNestedStyle('xAxis', 'fontWeight', s.xAxis?.fontWeight === 'bold' ? 'normal' : 'bold')}
                            className={`flex-1 border border-slate-200 rounded p-1 text-xs font-bold transition-colors ${s.xAxis?.fontWeight === 'bold' ? 'bg-blue-50 text-blue-600 border-blue-200' : 'hover:bg-slate-50'}`}
                        >
                            B
                        </button>
                        <button
                            onClick={() => updateNestedStyle('xAxis', 'fontStyle', s.xAxis?.fontStyle === 'italic' ? 'normal' : 'italic')}
                            className={`flex-1 border border-slate-200 rounded p-1 text-xs italic transition-colors ${s.xAxis?.fontStyle === 'italic' ? 'bg-blue-50 text-blue-600 border-blue-200' : 'hover:bg-slate-50'}`}
                        >
                            I
                        </button>
                    </div>

                    {/* X-Axis Colors */}
                    <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-100">
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 mb-1">Label</label>
                            <input
                                type="color"
                                className="w-full h-6 rounded border border-slate-200 cursor-pointer"
                                value={s.xAxis?.labelColor || GLOBAL_DEFAULTS.xAxis.labelColor}
                                onChange={(e) => updateNestedStyle('xAxis', 'labelColor', e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 mb-1">Line</label>
                            <input
                                type="color"
                                className="w-full h-6 rounded border border-slate-200 cursor-pointer"
                                value={s.xAxis?.lineColor || GLOBAL_DEFAULTS.xAxis.lineColor}
                                onChange={(e) => updateNestedStyle('xAxis', 'lineColor', e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 mb-1">Grid</label>
                            <input
                                type="color"
                                className="w-full h-6 rounded border border-slate-200 cursor-pointer"
                                value={s.xAxis?.gridColor || GLOBAL_DEFAULTS.xAxis.gridColor}
                                onChange={(e) => updateNestedStyle('xAxis', 'gridColor', e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            </Accordion>}

            {/* 3. Y-AXIS (Hidden for non-cartesian widgets) */}
            {!['filter', 'table', 'pivot', 'text', 'kpi', 'gauge', 'pie', 'donut', 'funnel', 'treemap', 'sankey', 'ragKpi', 'sparkline'].includes(widget.type) &&
                <Accordion
                    title={s.bar?.horizontal ? "Horizontal Axis (Values)" : "Y-Axis"}
                    icon={<ArrowUp className="w-3 h-3 text-blue-500" />}
                    isOpen={openSection === 'yaxis'}
                    onToggle={() => setOpenSection(openSection === 'yaxis' ? null : 'yaxis')}
                >
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <span className="text-xs text-slate-600">Show Axis</span>
                            <Toggle
                                checked={s.yAxis?.show !== false}
                                onChange={(chk) => updateNestedStyle('yAxis', 'show', chk)}
                            />
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-xs text-slate-600">Gridlines</span>
                            <Toggle
                                checked={s.yAxis?.showGrid !== false}
                                onChange={(chk) => updateNestedStyle('yAxis', 'showGrid', chk)}
                            />
                        </div>

                        {/* Per-Series Formatting Loop (Y-Axis + Tooltip Fields) */}
                        <div className="space-y-3 pt-2 border-t border-slate-100">
                            <span className="text-xs font-bold text-slate-600 block">Value Formatting</span>
                            {(() => {
                                if (ySeries.length === 0) {
                                    return <p className="text-[10px] text-slate-400 italic">Add measures to configure formatting.</p>;
                                }

                                return ySeries.map((series) => (
                                    <FormatSettings
                                        key={series.id}
                                        label={series.name}
                                        value={s.seriesFormats?.[series.id]}
                                        onChange={(newFmt) => updateNestedStyle('seriesFormats', series.id, newFmt)}
                                    />
                                ));
                            })()}
                        </div>

                        <div className="flex items-center justify-between">
                            <span className="text-xs text-slate-600">Log Scale</span>
                            <Toggle
                                checked={s.yAxis?.logScale}
                                onChange={(chk) => updateNestedStyle('yAxis', 'logScale', chk)}
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                {/* Font Size moved here to match X-Axis layout if desired, but user asked for simple font controls. Keeping it close to others. */}
                                <label className="block text-[10px] font-bold text-slate-400 mb-1">Font Size</label>
                                <input
                                    type="number"
                                    className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                    value={s.yAxis?.fontSize || 10}
                                    onChange={(e) => updateNestedStyle('yAxis', 'fontSize', Number(e.target.value))}
                                />
                            </div>
                        </div>

                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <span className="text-xs text-slate-600">Title</span>
                                <Toggle
                                    checked={!!s.yAxis?.showTitle}
                                    onChange={(chk) => updateNestedStyle('yAxis', 'showTitle', chk)}
                                />
                            </div>
                            {s.yAxis?.showTitle && (
                                <input
                                    type="text"
                                    className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                    placeholder="Axis Title"
                                    value={s.yAxis?.titleText || ''}
                                    onChange={(e) => updateNestedStyle('yAxis', 'titleText', e.target.value)}
                                />
                            )}
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => updateNestedStyle('yAxis', 'fontWeight', s.yAxis?.fontWeight === 'bold' ? 'normal' : 'bold')}
                                className={`flex-1 border border-slate-200 rounded p-1 text-xs font-bold transition-colors ${s.yAxis?.fontWeight === 'bold' ? 'bg-blue-50 text-blue-600 border-blue-200' : 'hover:bg-slate-50'}`}
                            >
                                B
                            </button>
                            <button
                                onClick={() => updateNestedStyle('yAxis', 'fontStyle', s.yAxis?.fontStyle === 'italic' ? 'normal' : 'italic')}
                                className={`flex-1 border border-slate-200 rounded p-1 text-xs italic transition-colors ${s.yAxis?.fontStyle === 'italic' ? 'bg-blue-50 text-blue-600 border-blue-200' : 'hover:bg-slate-50'}`}
                            >
                                I
                            </button>
                        </div>

                        {/* Y-Axis Colors */}
                        <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-100">
                            <div>
                                <label className="block text-[10px] font-bold text-slate-400 mb-1">Label</label>
                                <input
                                    type="color"
                                    className="w-full h-6 rounded border border-slate-200 cursor-pointer"
                                    value={s.yAxis?.labelColor || GLOBAL_DEFAULTS.yAxis.labelColor}
                                    onChange={(e) => updateNestedStyle('yAxis', 'labelColor', e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-slate-400 mb-1">Line</label>
                                <input
                                    type="color"
                                    className="w-full h-6 rounded border border-slate-200 cursor-pointer"
                                    value={s.yAxis?.lineColor || GLOBAL_DEFAULTS.yAxis.lineColor}
                                    onChange={(e) => updateNestedStyle('yAxis', 'lineColor', e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-slate-400 mb-1">Grid</label>
                                <input
                                    type="color"
                                    className="w-full h-6 rounded border border-slate-200 cursor-pointer"
                                    value={s.yAxis?.gridColor || GLOBAL_DEFAULTS.yAxis.gridColor}
                                    onChange={(e) => updateNestedStyle('yAxis', 'gridColor', e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                </Accordion>}

            {/* 3.5 SECONDARY Y-AXIS (For Combo) */}
            {widget.type === 'combo' &&
                <Accordion
                    title="Secondary Y-Axis (Right)"
                    icon={<ArrowUp className="w-3 h-3 text-indigo-500" />}
                    isOpen={openSection === 'secondaryYAxis'}
                    onToggle={() => setOpenSection(openSection === 'secondaryYAxis' ? null : 'secondaryYAxis')}
                >
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <span className="text-xs text-slate-600">Show Axis</span>
                            <Toggle
                                checked={s.secondaryYAxis?.show !== false}
                                onChange={(chk) => updateNestedStyle('secondaryYAxis', 'show', chk)}
                            />
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-xs text-slate-600">Gridlines</span>
                            <Toggle
                                checked={s.secondaryYAxis?.showGrid === true} // Default false for secondary
                                onChange={(chk) => updateNestedStyle('secondaryYAxis', 'showGrid', chk)}
                            />
                        </div>

                        <div className="flex items-center justify-between">
                            <span className="text-xs text-slate-600">Log Scale</span>
                            <Toggle
                                checked={s.secondaryYAxis?.logScale}
                                onChange={(chk) => updateNestedStyle('secondaryYAxis', 'logScale', chk)}
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="block text-[10px] font-bold text-slate-400 mb-1">Font Size</label>
                                <input
                                    type="number"
                                    className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                    value={s.secondaryYAxis?.fontSize || 10}
                                    onChange={(e) => updateNestedStyle('secondaryYAxis', 'fontSize', Number(e.target.value))}
                                />
                            </div>
                        </div>

                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <span className="text-xs text-slate-600">Title</span>
                                <Toggle
                                    checked={!!s.secondaryYAxis?.showTitle}
                                    onChange={(chk) => updateNestedStyle('secondaryYAxis', 'showTitle', chk)}
                                />
                            </div>
                            {s.secondaryYAxis?.showTitle && (
                                <input
                                    type="text"
                                    className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                    placeholder="Axis Title"
                                    value={s.secondaryYAxis?.titleText || ''}
                                    onChange={(e) => updateNestedStyle('secondaryYAxis', 'titleText', e.target.value)}
                                />
                            )}
                        </div>

                        {/* Colors */}
                        <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-100">
                            <div>
                                <label className="block text-[10px] font-bold text-slate-400 mb-1">Label</label>
                                <input
                                    type="color"
                                    className="w-full h-6 rounded border border-slate-200 cursor-pointer"
                                    value={s.secondaryYAxis?.labelColor || '#6366f1'}
                                    onChange={(e) => updateNestedStyle('secondaryYAxis', 'labelColor', e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-slate-400 mb-1">Line</label>
                                <input
                                    type="color"
                                    className="w-full h-6 rounded border border-slate-200 cursor-pointer"
                                    value={s.secondaryYAxis?.lineColor || '#6366f1'}
                                    onChange={(e) => updateNestedStyle('secondaryYAxis', 'lineColor', e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                </Accordion>
            }

            {/* 4. LEGEND (Hidden for simpler widgets) */}
            {!['filter', 'table', 'pivot', 'text', 'kpi', 'gauge', 'ragKpi', 'sparkline', 'sankey', 'treemap', 'funnel'].includes(widget.type) && <Accordion
                title="Legend"
                icon={<List className="w-3 h-3 text-blue-500" />}
                isOpen={openSection === 'legend'}
                onToggle={() => setOpenSection(openSection === 'legend' ? null : 'legend')}
            >
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-600">Show Legend</span>
                        <Toggle
                            checked={s.legend?.show !== false}
                            onChange={(chk) => updateNestedStyle('legend', 'show', chk)}
                        />
                    </div>
                    <div>
                        <label className="block text-[10px] font-bold text-slate-400 mb-1">Position</label>
                        <select
                            className="w-full text-xs p-1.5 border border-slate-200 rounded"
                            value={JSON.stringify({ v: s.legend?.vPosition, h: s.legend?.hPosition, t: s.legend?.type })}
                            onChange={(e) => {
                                const val = JSON.parse(e.target.value || '{}');
                                onWidgetUpdate(widget.id, {
                                    config: {
                                        ...widget.config, style: {
                                            ...s, legend: {
                                                ...s.legend,
                                                vPosition: val.v,
                                                hPosition: val.h,
                                                type: val.t // 'scroll' or 'plain' usually, but here we used 'custom' logic?
                                            }
                                        }
                                    }
                                });
                            }}
                        >
                            <option value={JSON.stringify({ v: 'bottom', h: 'center' })}>Bottom Center</option>
                            <option value={JSON.stringify({ v: 'bottom', h: 'left' })}>Bottom Left</option>
                            <option value={JSON.stringify({ v: 'bottom', h: 'right' })}>Bottom Right</option>
                            <option value={JSON.stringify({ v: 'top', h: 'center' })}>Top Center</option>
                            <option value={JSON.stringify({ v: 'top', h: 'left' })}>Top Left</option>
                            <option value={JSON.stringify({ v: 'top', h: 'right' })}>Top Right</option>
                            <option value={JSON.stringify({ v: 'middle', h: 'right' })}>Right Middle</option>
                            <option value={JSON.stringify({ v: 'middle', h: 'left' })}>Left Middle</option>
                            <option value={JSON.stringify({ v: 'custom', h: 'custom', t: 'custom' })}>Custom</option>
                        </select>
                    </div>

                    {/* Custom Legend Position */}
                    {s.legend?.hPosition === 'custom' && (
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="block text-[10px] font-bold text-slate-400 mb-1">Left (%)</label>
                                <input
                                    type="number" className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                    value={s.legend?.customLeft || 0}
                                    onChange={(e) => updateNestedStyle('legend', 'customLeft', Number(e.target.value))}
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-slate-400 mb-1">Top (%)</label>
                                <input
                                    type="number" className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                    value={s.legend?.customTop || 0}
                                    onChange={(e) => updateNestedStyle('legend', 'customTop', Number(e.target.value))}
                                />
                            </div>
                        </div>
                    )}

                    <div className="space-y-2 pt-2 border-t border-slate-100">
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="block text-[10px] font-bold text-slate-400 mb-1">Font Size</label>
                                <input
                                    type="number"
                                    className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                    value={s.legend?.fontSize || 12}
                                    onChange={(e) => updateNestedStyle('legend', 'fontSize', Number(e.target.value))}
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-slate-400 mb-1">Font Color</label>
                                <input
                                    type="color"
                                    className="w-full h-[30px] rounded border border-slate-200 cursor-pointer"
                                    value={s.legend?.fontColor || GLOBAL_DEFAULTS.legend.fontColor}
                                    onChange={(e) => updateNestedStyle('legend', 'fontColor', e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => updateNestedStyle('legend', 'fontWeight', s.legend?.fontWeight === 'bold' ? 'normal' : 'bold')}
                                className={`flex-1 border border-slate-200 rounded p-1 text-xs font-bold transition-colors ${s.legend?.fontWeight === 'bold' ? 'bg-blue-50 text-blue-600 border-blue-200' : 'hover:bg-slate-50'}`}
                            >
                                B
                            </button>
                            <button
                                onClick={() => updateNestedStyle('legend', 'fontStyle', s.legend?.fontStyle === 'italic' ? 'normal' : 'italic')}
                                className={`flex-1 border border-slate-200 rounded p-1 text-xs italic transition-colors ${s.legend?.fontStyle === 'italic' ? 'bg-blue-50 text-blue-600 border-blue-200' : 'hover:bg-slate-50'}`}
                            >
                                I
                            </button>
                        </div>
                    </div>
                </div>
            </Accordion>}

            {/* 5. DATA LABELS (Hidden for simple widgets) */}
            {!['filter', 'table', 'text', 'kpi', 'gauge', 'ragKpi', 'sparkline', 'sankey', 'treemap', 'funnel', 'pie', 'donut'].includes(widget.type) && <Accordion
                title="Data Labels"
                icon={<Type className="w-3 h-3 text-blue-500" />}
                isOpen={openSection === 'datalabels'}
                onToggle={() => setOpenSection(openSection === 'datalabels' ? null : 'datalabels')}
            >
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-600">Show Labels</span>
                        <Toggle
                            checked={!!s.showDataLabel}
                            onChange={(chk) => updateStyle('showDataLabel', chk)}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 mb-1">Position</label>
                            <select
                                className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                value={s.dataLabel?.position || 'top'}
                                onChange={(e) => updateNestedStyle('dataLabel', 'position', e.target.value)}
                            >
                                <option value="top">Top</option>
                                <option value="inside">Inside</option>
                                <option value="outside">Outside</option>
                                <option value="center">Center</option>
                                <option value="left">Left</option>
                                <option value="right">Right</option>
                                <option value="bottom">Bottom</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 mb-1">Angle</label>
                            <input
                                type="number" min="-90" max="90"
                                className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                value={s.dataLabel?.rotate || 0}
                                onChange={(e) => updateNestedStyle('dataLabel', 'rotate', Number(e.target.value))}
                            />
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => updateNestedStyle('dataLabel', 'fontWeight', s.dataLabel?.fontWeight === 'bold' ? 'normal' : 'bold')}
                            className={`flex-1 border border-slate-200 rounded p-1 text-xs font-bold transition-colors ${s.dataLabel?.fontWeight === 'bold' ? 'bg-blue-50 text-blue-600 border-blue-200' : 'hover:bg-slate-50'}`}
                        >
                            B
                        </button>
                        <button
                            onClick={() => updateNestedStyle('dataLabel', 'fontStyle', s.dataLabel?.fontStyle === 'italic' ? 'normal' : 'italic')}
                            className={`flex-1 border border-slate-200 rounded p-1 text-xs italic transition-colors ${s.dataLabel?.fontStyle === 'italic' ? 'bg-blue-50 text-blue-600 border-blue-200' : 'hover:bg-slate-50'}`}
                        >
                            I
                        </button>
                    </div>

                    {/* Data Label Format REMOVED - Inherit from Axis/Series ONLY */}
                </div>
            </Accordion>}

            {/* 6. TOOLTIP (Hidden for simple widgets) */}
            {!['filter', 'text', 'kpi', 'gauge', 'ragKpi', 'sparkline'].includes(widget.type) && <Accordion
                title="Tooltip"
                icon={<MessageSquare className="w-3 h-3 text-blue-500" />}
                isOpen={openSection === 'tooltip'}
                onToggle={() => setOpenSection(openSection === 'tooltip' ? null : 'tooltip')}
            >
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-600">Show Tooltip</span>
                        <Toggle
                            checked={s.tooltip?.show !== false}
                            onChange={(chk) => updateNestedStyle('tooltip', 'show', chk)}
                        />
                    </div>
                    <div>
                        <label className="block text-[10px] font-bold text-slate-400 mb-1">Trigger Mode</label>
                        <select
                            className="w-full text-xs p-1.5 border border-slate-200 rounded"
                            value={s.tooltip?.trigger || 'item'}
                            onChange={(e) => updateNestedStyle('tooltip', 'trigger', e.target.value)}
                        >
                            <option value="item">Item (Data Point)</option>
                            <option value="axis">Axis (All Series)</option>
                        </select>
                    </div>



                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 mb-1">Background</label>
                            <div className="flex items-center gap-2">
                                <input
                                    type="color"
                                    className="w-full h-6 p-0 border border-slate-200 rounded cursor-pointer"
                                    value={s.tooltip?.backgroundColor || '#ffffff'}
                                    onChange={(e) => updateNestedStyle('tooltip', 'backgroundColor', e.target.value)}
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 mb-1">Opacity</label>
                            <input
                                type="number" step="0.1" min="0" max="1"
                                className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                value={s.tooltip?.opacity ?? 0.9}
                                onChange={(e) => updateNestedStyle('tooltip', 'opacity', Number(e.target.value))}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 mb-1">Font Size</label>
                            <input
                                type="number"
                                className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                value={s.tooltip?.fontSize || 12}
                                onChange={(e) => updateNestedStyle('tooltip', 'fontSize', Number(e.target.value))}
                            />
                        </div>
                    </div>

                    <div className="flex gap-2">
                        <button
                            onClick={() => updateNestedStyle('tooltip', 'fontWeight', s.tooltip?.fontWeight === 'bold' ? 'normal' : 'bold')}
                            className={`flex-1 border border-slate-200 rounded p-1 text-xs font-bold transition-colors ${s.tooltip?.fontWeight === 'bold' ? 'bg-blue-50 text-blue-600 border-blue-200' : 'hover:bg-slate-50'}`}
                        >
                            B
                        </button>
                        <button
                            onClick={() => updateNestedStyle('tooltip', 'fontStyle', s.tooltip?.fontStyle === 'italic' ? 'normal' : 'italic')}
                            className={`flex-1 border border-slate-200 rounded p-1 text-xs italic transition-colors ${s.tooltip?.fontStyle === 'italic' ? 'bg-blue-50 text-blue-600 border-blue-200' : 'hover:bg-slate-50'}`}
                        >
                            I
                        </button>
                    </div>

                    <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-600">Border</span>
                        <Toggle
                            checked={!!s.tooltip?.borderColor}
                            onChange={(chk) => updateNestedStyle('tooltip', 'borderColor', chk ? '#cccccc' : '')}
                        />
                    </div>
                    <div className="pt-2 border-t border-slate-100">
                        <span className="text-xs font-bold text-slate-600 block mb-2">Value Formatting</span>

                        {/* Global Tooltip Format */}
                        <div className="mb-3 pb-3 border-b border-slate-50">
                            <FormatSettings
                                label="Default Format"
                                value={s.tooltip?.valueFormat}
                                onChange={(newFmt) => updateNestedStyle('tooltip', 'valueFormat', newFmt)}
                            />
                        </div>

                        {/* Extra Fields Formatting */}
                        {s.tooltip?.extraFields?.length > 0 && (
                            <div className="space-y-3">
                                {s.tooltip.extraFields.map(f => (
                                    <FormatSettings
                                        key={f}
                                        label={f}
                                        value={s.seriesFormats?.[f]}
                                        onChange={(newFmt) => updateNestedStyle('seriesFormats', f, newFmt)}
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Additional Measures Selection (Bottom) */}
                    {dataset && (
                        <div className="pt-2 border-t border-slate-100">
                            <label className="block text-[10px] font-bold text-slate-400 mb-1">Additional Info</label>

                            {/* Dropdown for Selection */}
                            <select
                                className="w-full text-xs p-1.5 border border-slate-200 rounded mb-2"
                                value=""
                                onChange={(e) => {
                                    if (!e.target.value) return;
                                    const current = s.tooltip?.extraFields || [];
                                    if (!current.includes(e.target.value)) {
                                        updateNestedStyle('tooltip', 'extraFields', [...current, e.target.value]);
                                    }
                                }}
                            >
                                <option value="">+ Add Data Field...</option>
                                {dataset.measures?.filter(m => {
                                    const inX = widget.config.xAxis?.some(x => x.name === m.name);
                                    const inY = ySeries.some(y => y.name === m.name);
                                    const alreadyAdded = (s.tooltip?.extraFields || []).includes(m.name);
                                    return !inX && !inY && !alreadyAdded;
                                }).map(m => (
                                    <option key={m.name} value={m.name}>{m.name}</option>
                                ))}
                            </select>

                            {/* Tags List */}
                            <div className="flex flex-wrap gap-1">
                                {s.tooltip?.extraFields?.map(f => (
                                    <span key={f} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-slate-100 text-slate-600 border border-slate-200">
                                        {f}
                                        <button
                                            onClick={() => {
                                                const current = s.tooltip?.extraFields || [];
                                                updateNestedStyle('tooltip', 'extraFields', current.filter(x => x !== f));
                                            }}
                                            className="ml-1 hover:text-red-500"
                                        >
                                            &times;
                                        </button>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </Accordion >}

            {/* TEXT WIDGET STYLING */}
            {widget.type === 'text' && (
                <Accordion
                    title="Text Styling"
                    isOpen={openSection === 'textStyle'}
                    onToggle={() => setOpenSection(openSection === 'textStyle' ? null : 'textStyle')}
                    icon={<Type className="w-3 h-3 text-blue-500" />}
                >
                    <div className="space-y-4">
                        {/* Font Settings */}
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="block text-[10px] font-bold text-slate-400 mb-1">Font Size</label>
                                <input
                                    type="number"
                                    className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                    value={s.fontSize || 14}
                                    onChange={(e) => updateStyle('fontSize', Number(e.target.value))}
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-slate-400 mb-1">Padding</label>
                                <input
                                    type="number"
                                    className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                    value={s.padding || 12}
                                    onChange={(e) => updateStyle('padding', Number(e.target.value))}
                                />
                            </div>
                        </div>

                        {/* Alignment & Decoration */}
                        <div className="flex gap-2">
                            {/* Decoration */}
                            <div className="flex border border-slate-200 rounded p-0.5 bg-slate-50">
                                <button onClick={() => updateStyle('bold', !s.bold)} className={`px-2 py-1 text-[10px] rounded ${s.bold ? 'bg-white shadow text-blue-600 font-bold' : 'text-slate-500'}`}>B</button>
                                <button onClick={() => updateStyle('italic', !s.italic)} className={`px-2 py-1 text-[10px] rounded ${s.italic ? 'bg-white shadow text-blue-600 italic' : 'text-slate-500'}`}>I</button>
                                <button onClick={() => updateStyle('underline', !s.underline)} className={`px-2 py-1 text-[10px] rounded ${s.underline ? 'bg-white shadow text-blue-600 underline' : 'text-slate-500'}`}>U</button>
                            </div>
                            {/* Alignment */}
                            <div className="flex border border-slate-200 rounded p-0.5 bg-slate-50 ml-auto">
                                {['left', 'center', 'right'].map(align => (
                                    <button
                                        key={align}
                                        onClick={() => updateStyle('align', align)}
                                        className={`px-2 py-1 text-[10px] rounded capitalize ${s.align === align ? 'bg-white shadow text-blue-600 font-bold' : 'text-slate-500'}`}
                                    >
                                        {align.charAt(0).toUpperCase()}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Colors */}
                        <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100">
                            <div>
                                <label className="block text-[10px] font-bold text-slate-400 mb-1">Text Color</label>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="color"
                                        className="w-6 h-6 p-0 border border-slate-200 rounded cursor-pointer"
                                        value={s.color || '#1e293b'}
                                        onChange={(e) => updateStyle('color', e.target.value)}
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-slate-400 mb-1">Background</label>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="color"
                                        className="w-6 h-6 p-0 border border-slate-200 rounded cursor-pointer"
                                        value={s.backgroundColor || '#ffffff'} // Default explicit white or transparent handling logic needed if user wants pure transparent
                                        onChange={(e) => updateStyle('backgroundColor', e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </Accordion>
            )}

            {/* IMAGE WIDGET SETTINGS */}
            {widget.type === 'image' && (
                <Accordion
                    title="Image Settings"
                    isOpen={openSection === 'imageSettings'}
                    onToggle={() => setOpenSection(openSection === 'imageSettings' ? null : 'imageSettings')}
                    icon={<Image className="w-3 h-3 text-teal-500" />}
                >
                    <div className="space-y-4">
                        {/* URL Input */}
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 mb-1">Image URL</label>
                            <input
                                type="url"
                                className="w-full text-xs p-1.5 border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-[#118DF0]"
                                placeholder="https://example.com/image.png"
                                value={widget.config?.image?.imageUrl || ''}
                                onChange={(e) => onWidgetUpdate(widget.id, {
                                    config: { ...widget.config, image: { ...(widget.config?.image || {}), imageUrl: e.target.value } }
                                })}
                            />
                            <p className="text-[10px] text-slate-400 mt-1">Paste any public image URL</p>
                        </div>

                        {/* Object Fit */}
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 mb-1">Fit Mode</label>
                            <div className="flex border border-slate-200 rounded overflow-hidden text-[10px]">
                                {(['contain', 'cover', 'fill'] as const).map(fit => (
                                    <button
                                        key={fit}
                                        onClick={() => onWidgetUpdate(widget.id, {
                                            config: { ...widget.config, image: { ...(widget.config?.image || {}), objectFit: fit } }
                                        })}
                                        className={`flex-1 py-1 capitalize transition-colors ${(widget.config?.image?.objectFit || 'contain') === fit ? 'bg-[#118DF0] text-white font-semibold' : 'hover:bg-slate-50 text-slate-600'}`}
                                    >
                                        {fit}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Alignment */}
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 mb-1">Alignment</label>
                            <div className="flex border border-slate-200 rounded overflow-hidden text-[10px]">
                                {(['left', 'center', 'right'] as const).map(align => (
                                    <button
                                        key={align}
                                        onClick={() => onWidgetUpdate(widget.id, {
                                            config: { ...widget.config, image: { ...(widget.config?.image || {}), align } }
                                        })}
                                        className={`flex-1 py-1 capitalize transition-colors ${(widget.config?.image?.align || 'center') === align ? 'bg-[#118DF0] text-white font-semibold' : 'hover:bg-slate-50 text-slate-600'}`}
                                    >
                                        {align}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Opacity */}
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <label className="text-[10px] font-bold text-slate-400">Opacity</label>
                                <span className="text-[10px] text-slate-500">{widget.config?.image?.opacity ?? 100}%</span>
                            </div>
                            <input
                                type="range"
                                min={10}
                                max={100}
                                step={5}
                                className="w-full accent-[#118DF0]"
                                value={widget.config?.image?.opacity ?? 100}
                                onChange={(e) => onWidgetUpdate(widget.id, {
                                    config: { ...widget.config, image: { ...(widget.config?.image || {}), opacity: Number(e.target.value) } }
                                })}
                            />
                        </div>

                        {/* Border */}
                        <div className="space-y-2 pt-2 border-t border-slate-100">
                            <div className="flex items-center justify-between">
                                <span className="text-xs text-slate-600">Show Border</span>
                                <Toggle
                                    checked={widget.config?.image?.showBorder || false}
                                    onChange={(chk) => onWidgetUpdate(widget.id, {
                                        config: { ...widget.config, image: { ...(widget.config?.image || {}), showBorder: chk } }
                                    })}
                                />
                            </div>
                            {widget.config?.image?.showBorder && (
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-400 mb-1">Border Color</label>
                                    <input
                                        type="color"
                                        className="w-full h-6 rounded border border-slate-200 cursor-pointer"
                                        value={widget.config?.image?.borderColor || '#d6d6d6'}
                                        onChange={(e) => onWidgetUpdate(widget.id, {
                                            config: { ...widget.config, image: { ...(widget.config?.image || {}), borderColor: e.target.value } }
                                        })}
                                    />
                                </div>
                            )}
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <label className="text-[10px] font-bold text-slate-400">Corner Radius</label>
                                    <span className="text-[10px] text-slate-500">{widget.config?.image?.borderRadius ?? 0}px</span>
                                </div>
                                <input
                                    type="range"
                                    min={0}
                                    max={24}
                                    className="w-full accent-[#118DF0]"
                                    value={widget.config?.image?.borderRadius ?? 0}
                                    onChange={(e) => onWidgetUpdate(widget.id, {
                                        config: { ...widget.config, image: { ...(widget.config?.image || {}), borderRadius: Number(e.target.value) } }
                                    })}
                                />
                            </div>
                        </div>
                    </div>
                </Accordion>
            )}

            {/* 7. GAUGE / RAG SCALE (New) */}
            {['gauge', 'ragKpi'].includes(widget.type) && <Accordion
                title="Scale & Targets"
                icon={<Gauge className="w-3 h-3 text-blue-500" />}
                isOpen={openSection === 'scale'}
                onToggle={() => setOpenSection(openSection === 'scale' ? null : 'scale')}
            >
                <div className="space-y-3">
                    <p className="text-[10px] text-slate-400 italic leading-snug">
                        Manually override values if no dynamic field is set.
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 mb-1">Min Value</label>
                            <input
                                type="number"
                                className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                placeholder="Auto"
                                value={s.gauge?.min ?? ''}
                                onChange={(e) => updateNestedStyle('gauge', 'min', e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 mb-1">Max Value</label>
                            <input
                                type="number"
                                className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                placeholder="Auto"
                                value={s.gauge?.max ?? ''}
                                onChange={(e) => updateNestedStyle('gauge', 'max', e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 mb-1">Target Value</label>
                            <input
                                type="number"
                                className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                placeholder="Optional"
                                value={s.gauge?.target ?? ''}
                                onChange={(e) => updateNestedStyle('gauge', 'target', e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 mb-1">Target Label</label>
                            <input
                                type="text"
                                className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                placeholder="e.g. Budget"
                                value={s.gauge?.targetLabel || ''}
                                onChange={(e) => updateNestedStyle('gauge', 'targetLabel', e.target.value)}
                            />
                        </div>
                    </div>
                </div>
            </Accordion>}

            {/* 8. RAG VISUALS (New) */}
            {['ragKpi'].includes(widget.type) && <Accordion
                title="RAG Visuals"
                icon={<Palette className="w-3 h-3 text-blue-500" />}
                isOpen={openSection === 'ragVisuals'}
                onToggle={() => setOpenSection(openSection === 'ragVisuals' ? null : 'ragVisuals')}
            >
                <div className="space-y-4">
                    {/* Sentiment Logic */}
                    <div>
                        <label className="block text-[10px] font-bold text-slate-400 mb-1">Sentiment Logic</label>
                        <select
                            className="w-full text-xs p-1.5 border border-slate-200 rounded"
                            value={s.rag?.sentiment || 'normal'}
                            onChange={(e) => updateNestedStyle('rag', 'sentiment', e.target.value)}
                        >
                            <option value="normal">Higher is Better (Green {'>'} Red)</option>
                            <option value="inverse">Lower is Better (Red {'>'} Green)</option>
                        </select>
                    </div>

                    {/* Colors */}
                    <div className="grid grid-cols-3 gap-2">
                        <div>
                            <label className="block text-[10px] text-slate-400 mb-1">Critical</label>
                            <input
                                type="color"
                                className="w-full h-6 p-0 border border-slate-200 rounded cursor-pointer"
                                value={s.rag?.criticalColor || '#ef4444'}
                                onChange={(e) => updateNestedStyle('rag', 'criticalColor', e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] text-slate-400 mb-1">Warning</label>
                            <input
                                type="color"
                                className="w-full h-6 p-0 border border-slate-200 rounded cursor-pointer"
                                value={s.rag?.warningColor || '#f59e0b'}
                                onChange={(e) => updateNestedStyle('rag', 'warningColor', e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] text-slate-400 mb-1">Healthy</label>
                            <input
                                type="color"
                                className="w-full h-6 p-0 border border-slate-200 rounded cursor-pointer"
                                value={s.rag?.healthyColor || '#22c55e'}
                                onChange={(e) => updateNestedStyle('rag', 'healthyColor', e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Icon Size */}
                    <div className="pt-2 border-t border-slate-100">
                        <div className="flex items-center justify-between mb-1">
                            <label className="text-[10px] font-bold text-slate-400">Icon Size</label>
                            <span className="text-[10px] text-slate-500">{s.rag?.iconSize || 64}px</span>
                        </div>
                        <input
                            type="range"
                            min="24"
                            max="128"
                            step="8"
                            className="w-full h-1 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                            value={s.rag?.iconSize || 64}
                            onChange={(e) => updateNestedStyle('rag', 'iconSize', Number(e.target.value))}
                        />
                    </div>

                    {/* Shapes & Visibility */}
                    <div className="space-y-2 pt-2 border-t border-slate-100">
                        <div className="flex items-center justify-between">
                            <span className="text-xs text-slate-600">Icon Shape</span>
                            <div className="flex border border-slate-200 rounded p-0.5 bg-slate-50">
                                {['circle', 'square', 'rounded'].map(shape => (
                                    <button
                                        key={shape}
                                        onClick={() => updateNestedStyle('rag', 'iconShape', shape)}
                                        className={`px-2 py-1 text-[10px] rounded capitalize ${s.rag?.iconShape === shape ? 'bg-white shadow text-blue-600 font-bold' : 'text-slate-500'}`}
                                    >
                                        {shape.charAt(0).toUpperCase()}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Elements Toggle */}
                    <div className="space-y-2 pt-2 border-t border-slate-100">
                        <div className="flex items-center justify-between">
                            <span className="text-xs text-slate-600">Show Label</span>
                            <Toggle
                                checked={s.rag?.showLabel !== false}
                                onChange={(chk) => updateNestedStyle('rag', 'showLabel', chk)}
                            />
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-xs text-slate-600">Show Value</span>
                            <Toggle
                                checked={s.rag?.showValue !== false}
                                onChange={(chk) => updateNestedStyle('rag', 'showValue', chk)}
                            />
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-xs text-slate-600">Show Status Text</span>
                            <Toggle
                                checked={s.rag?.showStatusText !== false} // e.g. "Healthy", "Critical" inside the bubble
                                onChange={(chk) => updateNestedStyle('rag', 'showStatusText', chk)}
                            />
                        </div>
                    </div>
                </div>
            </Accordion>}

            {/* 9. VALUE FORMATTING (For Specialized Widgets where Y-Axis is Hidden) */}
            {['kpi', 'gauge', 'ragKpi', 'sparkline'].includes(widget.type) && <Accordion
                title="Value Formatting"
                icon={<Hash className="w-3 h-3 text-blue-500" />}
                isOpen={openSection === 'valformat'}
                onToggle={() => setOpenSection(openSection === 'valformat' ? null : 'valformat')}
            >
                <div className="space-y-3">
                    <span className="text-xs font-bold text-slate-600 block">Metric Format</span>
                    {(() => {
                        const metrics = [
                            ...(widget.config?.yAxis || []),
                            ...(widget.config?.target || []),
                            ...(widget.type === 'kpi' ? (widget.config?.comparison || []) : [])
                        ];

                        if (metrics.length === 0) {
                            return <p className="text-[10px] text-slate-400 italic">Add metrics to configure formatting.</p>;
                        }

                        return metrics.map((series) => (
                            <FormatSettings
                                key={series.id}
                                label={series.name}
                                value={s.seriesFormats?.[series.id]}
                                onChange={(newFmt) => updateNestedStyle('seriesFormats', series.id, newFmt)}
                            />
                        ));
                    })()}
                </div>
            </Accordion>}

            {/* 8. PLOT AREA (Chart Internal Area - Hidden for filter widgets) */}
            {!['filter', 'text'].includes(widget.type) && <Accordion
                title="Plot Area"
                icon={<Maximize className="w-3 h-3 text-blue-500" />}
                isOpen={openSection === 'plot'}
                onToggle={() => setOpenSection(openSection === 'plot' ? null : 'plot')}
            >
                <div className="space-y-4">
                    {/* Plot Background (New Request) */}
                    <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-600">Plot Background</span>
                        <div className="flex items-center gap-2">
                            <input
                                type="color"
                                className="w-6 h-6 p-0 border border-slate-200 rounded cursor-pointer"
                                value={s.plotArea?.backgroundColor || '#ffffff'}
                                onChange={(e) => updateNestedStyle('plotArea', 'backgroundColor', e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-600">Show Border</span>
                        <input
                            type="checkbox"
                            checked={s.plotArea?.showBorder === true}
                            onChange={(e) => updateNestedStyle('plotArea', 'showBorder', e.target.checked)}
                            className="w-4 h-4"
                        />
                    </div>
                    {s.plotArea?.showBorder === true && (
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 mb-1">Border Color</label>
                            <input
                                type="color"
                                className="w-full h-6 p-0 border border-slate-200 rounded cursor-pointer"
                                value={s.plotArea?.borderColor || '#e2e8f0'}
                                onChange={(e) => updateNestedStyle('plotArea', 'borderColor', e.target.value)}
                            />
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 mb-1">Padding Top</label>
                            <input
                                type="number"
                                className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                value={s.plotArea?.paddingTop ?? 10}
                                onChange={(e) => updateNestedStyle('plotArea', 'paddingTop', Number(e.target.value))}
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 mb-1">Padding Bottom</label>
                            <input
                                type="number"
                                className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                value={s.plotArea?.paddingBottom ?? 10}
                                onChange={(e) => updateNestedStyle('plotArea', 'paddingBottom', Number(e.target.value))}
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 mb-1">Padding Left</label>
                            <input
                                type="number"
                                className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                value={s.plotArea?.paddingLeft ?? 10}
                                onChange={(e) => updateNestedStyle('plotArea', 'paddingLeft', Number(e.target.value))}
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 mb-1">Padding Right</label>
                            <input
                                type="number"
                                className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                value={s.plotArea?.paddingRight ?? 10}
                                onChange={(e) => updateNestedStyle('plotArea', 'paddingRight', Number(e.target.value))}
                            />
                        </div>
                    </div>
                </div>
            </Accordion>}

            {/* 8. WIDGET DESIGN (Card Container) */}
            <Accordion
                title="Widget Design"
                icon={<Layout className="w-3 h-3 text-blue-500" />}
                isOpen={openSection === 'widget'}
                onToggle={() => setOpenSection(openSection === 'widget' ? null : 'widget')}
            >
                <div className="space-y-4">
                    {/* Card Background (New Request) */}
                    <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-600">Card Background</span>
                        <div className="flex items-center gap-2">
                            <input
                                type="color"
                                className="w-6 h-6 p-0 border border-slate-200 rounded cursor-pointer"
                                value={s.design?.cardBackgroundColor || '#ffffff'}
                                onChange={(e) => updateNestedStyle('design', 'cardBackgroundColor', e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Shadow */}
                    <div>
                        <label className="block text-[10px] font-bold text-slate-400 mb-1">Shadow</label>
                        <select
                            className="w-full text-xs p-1.5 border border-slate-200 rounded"
                            value={s.design?.shadow || 'sm'}
                            onChange={(e) => updateNestedStyle('design', 'shadow', e.target.value)}
                        >
                            <option value="none">None</option>
                            <option value="0 1px 2px 0 rgb(0 0 0 / 0.05)">Small</option>
                            <option value="0 4px 6px -1px rgb(0 0 0 / 0.1)">Medium</option>
                            <option value="0 10px 15px -3px rgb(0 0 0 / 0.1)">Large</option>
                            <option value="0 20px 25px -5px rgb(0 0 0 / 0.1)">Extra Large</option>
                        </select>
                    </div>

                    {/* Corner Radius */}
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="block text-[10px] font-bold text-slate-400 mb-1">Corner Radius</label>
                            <input
                                type="number"
                                className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                value={s.design?.borderRadius ?? 8}
                                onChange={(e) => updateNestedStyle('design', 'borderRadius', Number(e.target.value))}
                            />
                        </div>
                    </div>

                    {/* Widget Border */}
                    <div className="pt-2 border-t border-slate-100">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-slate-600">Card Border</span>
                            <Toggle
                                checked={!!s.design?.border}
                                onChange={(chk) => updateNestedStyle('design', 'border', chk)}
                            />
                        </div>
                        {s.design?.border && (
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-400 mb-1">Width</label>
                                    <input
                                        type="number" min="1" max="10"
                                        className="w-full text-xs p-1.5 border border-slate-200 rounded"
                                        value={s.design?.borderWidth || 1}
                                        onChange={(e) => updateNestedStyle('design', 'borderWidth', Number(e.target.value))}
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-slate-400 mb-1">Color</label>
                                    <input
                                        type="color"
                                        className="w-full h-8 p-1 border border-slate-200 rounded cursor-pointer"
                                        value={s.design?.borderColor || '#e2e8f0'}
                                        onChange={(e) => updateNestedStyle('design', 'borderColor', e.target.value)}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Accordion>
        </div >
    );
};

// --- HELPERS ---

const Accordion = ({ title, icon = undefined, defaultOpen = false, isOpen = undefined, onToggle = undefined, children }) => {
    // Internal state if not controlled
    const [internalOpen, setInternalOpen] = useState(defaultOpen);

    // Use controlled state if provided, otherwise internal
    const isExpanded = isOpen !== undefined ? isOpen : internalOpen;
    const handleToggle = () => {
        if (onToggle) onToggle();
        else setInternalOpen(!internalOpen);
    };

    return (
        <div className="border border-slate-200 rounded-lg overflow-hidden bg-white mb-2 shadow-sm">
            <button
                onClick={handleToggle}
                className={`w-full px-3 py-2 flex items-center justify-between text-xs font-bold text-slate-700 hover:bg-slate-50 transition-colors ${isExpanded ? 'bg-slate-50 border-b border-slate-100' : 'bg-white'}`}
            >
                <div className="flex items-center gap-2">
                    {icon}
                    {title}
                </div>
                {isExpanded ? <ChevronDown className="w-3 h-3 text-slate-400" /> : <ChevronRight className="w-3 h-3 text-slate-400" />}
            </button>
            {isExpanded && (
                <div className="p-3 bg-white">
                    {children}
                </div>
            )}
        </div>
    );
};

const Section = ({ label, icon, children }) => (
    <div>
        <div className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 pl-1 flex items-center gap-2">
            {icon} {label}
        </div>
        {children}
    </div>
);

const ToolboxItem = ({ type, label, icon, onDragStart, onClick, onHover }) => (
    <div
        className="flex flex-col items-center justify-center p-1.5 bg-white border border-slate-200 rounded hover:bg-slate-50 group shadow-sm hover:shadow transition-all ring-1 ring-transparent hover:ring-blue-200 aspect-square relative"
        onMouseEnter={() => onHover && onHover(type)}
        onMouseLeave={() => onHover && onHover(null)}
    >
        <div
            draggable={true}
            onDragStart={(e) => onDragStart(e, type)}
            className="flex flex-col items-center justify-center flex-1 cursor-grab active:cursor-grabbing w-full h-full"
        >
            {icon}
            <span className="text-[9px] font-medium text-slate-500 mt-1.5 text-center leading-tight">{label}</span>
        </div>
        <button
            onClick={(e) => { e.stopPropagation(); onClick && onClick(); }}
            className="absolute top-0.5 right-0.5 p-0.5 text-slate-400 hover:text-blue-700 hover:bg-blue-100 rounded-full transition-all opacity-0 group-hover:opacity-100"
            title="Add to Dashboard"
        >
            <Plus className="w-2.5 h-2.5" strokeWidth={3} />
        </button>
    </div>
);

const DropZone = ({ label }) => (
    <div className="p-3 bg-slate-50 border-2 border-dashed border-slate-200 rounded-lg flex items-center justify-center text-slate-400 text-xs h-16 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600 transition-colors cursor-pointer">
        {label}
    </div>
);



const EmptyState = ({ label }) => (
    <div className="flex-1 flex items-center justify-center text-slate-400 border-2 border-dashed border-slate-200 rounded-lg h-32">
        <span className="text-xs">{label}</span>
    </div>
);

// --- TOOLTIPS & DESCRIPTIONS ---

const CHART_DESCRIPTIONS = {
    // Trends
    line: "Track trends and changes over time.",
    area: "Visualize cumulative magnitude of change.",
    stepLine: "Show distinct changes at specific intervals.",
    combo: "Combine bars and lines for dual-axis analysis.",
    forecast: "Project future trends based on historical data.",

    // Compare
    bar: "Compare values across categories.",
    horizontalBar: "Best for ranking with long category names.",
    groupedBar: "Compare multiple series side-by-side.",
    waterfall: "Understand cumulative effect of sequential values.",
    bullet: "Track progress against a target or goal.",

    // Composition
    pie: "Show proportions of a whole.",
    donut: "Show proportions with a focus on the arc length.",
    stackedBar: "Show part-to-whole relationships across categories.",
    stackedBar100: "Compare percentage contribution of each part.",
    treemap: "Display hierarchical data using nested rectangles.",

    // Performance
    kpi: "Highlight a single critical metric.",
    gauge: "Visualize a value within a defined range.",
    sparkline: "Miniature chart for inline trend visualization.",
    ragKpi: "Status indicator (Red/Amber/Green) based on thresholds.",

    // Flow
    sankey: "Visualize flow and volume between nodes.",
    funnel: "Track stages in a process (e.g., Sales Pipeline).",

    // Relationships
    scatter: "Analyze correlation between two variables.",
    bubble: "Visualize 3D data (X, Y, Size).",
    heatmap: "Visualize data density or intensity in a matrix.",

    // Geo
    map: "Visualize geographical distribution of data.",
    choropleth: "Shade regions based on data values.",

    // Details
    table: "Detailed row-level data view.",
    pivot: "Summarize and aggregate data across dimensions.",
    text: "Add rich text context or narration."
};

const GlobalTooltip = ({ type }) => {
    const [visibleType, setVisibleType] = useState(null);
    const showTimer = React.useRef(null);
    const hideTimer = React.useRef(null);

    useEffect(() => {
        // Clear pending timers on change
        clearTimeout(showTimer.current);
        clearTimeout(hideTimer.current);

        if (type) {
            // Start Delay
            showTimer.current = setTimeout(() => {
                setVisibleType(type);
                // Auto Hide after 3s
                hideTimer.current = setTimeout(() => setVisibleType(null), 3000);
            }, 2000);
        } else {
            // Immediate Clear if mouse leaves
            setVisibleType(null);
        }

        return () => {
            clearTimeout(showTimer.current);
            clearTimeout(hideTimer.current);
        };
    }, [type]);

    if (!visibleType) return null;

    return (
        <div className="absolute top-0 left-0 right-0 z-50 p-2 bg-slate-800 text-white text-xs font-medium text-center shadow-md animate-in slide-in-from-top-2 duration-200">
            <span className="font-bold uppercase text-[10px] text-slate-300 mr-2">{visibleType}:</span>
            {CHART_DESCRIPTIONS[visibleType]}
        </div>
    );
};

